import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "ui/card";
import { Button } from "ui/button";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import { Checkbox } from "ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "ui/dialog";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "ui/accordion";
import {
  Building2,
  Plus,
  Trash2,
  Save,
  DoorOpen,
  X,
  User,
  Settings,
  Clock,
  Edit2,
  AlertCircle,
  Info,
  Upload,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import {
  getAmenities,
  getCurrencies,
  getFloors,
  getPropertyGrades,
  getPropertyTypes,
  getRoomTypes,
  getVenues,
  createProperty,
  updateProperty,
  getPropertyById,
} from "services/propertyDetailsService";
import { removeFullSpecialCharacters } from "utils/commonutils";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface ContactInfo {
  id: string;
  name: string;
  label: string;
  email: string;
  phone: string;
}

interface ContractRoomType {
  id: string;
  roomTypeIds: string[];
  propertyRoomTypeId?: number;
  pricePerRoom: number;
  maxOccupancy: number;
  minOccupancy: number;
  minAdults: number;

  maxAdults: number;
  maxChildren: number;
  childAgeMax: number;
  numberOfRooms: number;
  roomNames?: string[];
  personTypes: string[];
  amenities?: Array<{
    id: number;
    name: string;
    isSelected: boolean;
    isChargeable: boolean;
    price: number;
  }>;
}

interface Contract {
  id: string;
  fromDate: string;
  toDate: string;
  rentAmount: number;
  roomTypeIds: string[];
  amenities: Array<{
    id: string;
    name: string;
    isSelected: boolean;
    isChargeable: boolean;
    price: number;
  }>;
  status: "active" | "inactive";
  inactiveReason?: string;
}

interface HotelContractDetails {
  hotelContractId?: number;
  publicId: string | null;
  contractReferenceNo: string;
  contractStartDate: string;
  contractEndDate: string;
  currency: string;
  billingCycle: string;
  paymentTerms: string;
  securityDeposit: number;
  taxPercentage: number;
  includedServices: string;
  cancellationPolicy: string;
  noticePeriodDays: number;
  agreementDocumentName: string;
  agreementDocumentUrl: string;
  agreementDocumentType: string;
  agreementDocumentSize: number;
}

interface AddedHotelContract extends HotelContractDetails {
  id: string;
  roomRentTotal: number;
  taxAmount: number;
  grandTotal: number;
  roomConfigs: ContractRoomType[];
  roomTypeRents: Array<{
    roomTypeId: number;
    roomTypeName: string;
    numberOfRooms: number;
    rentPerRoom: number;
    totalRent: number;
  }>;
}

const DEFAULT_HOTEL_CONTRACT: HotelContractDetails = {
  hotelContractId: undefined,
  publicId: null,
  contractReferenceNo: "",
  contractStartDate: "",
  contractEndDate: "",
  currency: "USD",
  billingCycle: "monthly",
  paymentTerms: "prepaid",
  securityDeposit: 0,
  taxPercentage: 0,
  includedServices: "",
  cancellationPolicy: "",
  noticePeriodDays: 30,
  agreementDocumentName: "",
  agreementDocumentUrl: "",
  agreementDocumentType: "",
  agreementDocumentSize: 0,
};

interface RoomDetail {
  id: string;
  srNo: number;
  roomNo: number;
  floor: number;
  type: string;
  baseCapacity: number;
  extraCapacity: number;
  bufferCapacity: number;
  isOccupied: boolean;
  maxOccupancy: number;
  maxAdults: number;
  maxChildren: number;
  childAgeMax: number;
  personType: string;
  amenities: Array<{
    id: number;
    name: string;
    isSelected: boolean;
    isChargeable: boolean;
    price: number;
  }>;
}

interface PropertyAmenity {
  id: number;
  name: string;
  isSelected: boolean;
  isChargeable: boolean;
  price: number;
}

interface EditingProperty {
  publicId: string;
  venueId: number;
  propertyName: string;
  propertyCode: string;
  address: string;
  primaryContact: string;
  primaryEmail: string;
  phoneNumber: string;
  propertyTypeId: number;
  propertyGradeId: number;
  totalRooms: number;
  totalFloors: number;
  isLiftAvailable: boolean;
  isHousekeepingAvailable: boolean;
  hasGroundFloor: boolean;
  hasBasement: boolean;
  checkInTime: string;
  checkOutTime: string;
  earlyCheckInTime: string;
  lateCheckInTime: string;
  lateCheckOutHours: string;
  earlyCheckInHours: string;
  lateCheckInHours: string;
  isActive: boolean;
  childAgeMax: number;
  amenityIds: number[];
  contacts: ContactInfo[];
  roomTypes: EditingRoomType[];
  hotelContracts: AddedHotelContract[];
}
interface AddPropertyPageProps {
  onNavigate?: (page: string) => void;
  onSaveProperty?: (property: EditingProperty) => void;
  editingProperty?: EditingProperty;
}

interface HotelRoomTypeRent {
  roomTypeId: number;
  roomTypeName: string;
  numberOfRooms: number;
  rentPerRoom: number;
  totalRent: number;
}

interface AddedHotelContract extends HotelContractDetails {
  id: string;
  roomRentTotal: number;
  taxAmount: number;
  grandTotal: number;
  roomTypeRents: HotelRoomTypeRent[];
}

interface EditingRoomType {
  roomTypeId: number;
  propertyRoomTypeId?: number;
  roomNamesArray?: string[];
  roomNames?: string;
  numberOfRooms: number;
  minAdults: number;
  minOccupancy: number;
  maxAdults: number;
  maxChildren: number;
  maxOccupancy: number;
  amenityIds: number[];
}

export const AddPropertyPage = ({
  onNavigate,
  onSaveProperty,
}: AddPropertyPageProps) => {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const { publicId } = useParams();
  const [activeTab, setActiveTab] = useState("basic");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [editingProperty, setEditingProperty] = useState<any>(null);

  useEffect(() => {
    setPageHeader({
      title: "Add Property Details",
      childTitle: editingProperty
        ? "Update property information, configuration and contracts"
        : "Add new property with complete configuration and contracts",
      icon: <Building2 className="h-4 w-4" />,
      onBack: () => navigate("/property-details"),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader, editingProperty]);

  useEffect(() => {
    const fetchProperty = async () => {
      if (!publicId) return;

      try {
        setLoading(true);
        const data = await getPropertyById(publicId);
        setEditingProperty(data);
      } catch (err) {
        toast.error("Failed to load property");
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [publicId]);

  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const data = await getAmenities();

        const propertyList: PropertyAmenity[] = [];
        const roomList: PropertyAmenity[] = [];

        data.forEach((amenity) => {
          if (!amenity.isActive) return;

          const formatted = {
            id: amenity.amenityId,
            name: amenity.amenityName.trim(),
            isSelected: false,
            isChargeable: false,
            price: 0,
          };

          if (amenity.amenityType === "Property") {
            propertyList.push(formatted);
          }

          if (amenity.amenityType === "Room") {
            roomList.push(formatted);
          }
        });

        setPropertyAmenities((prev) => {
          return propertyList.map((a) => {
            const existing = prev.find((p) => p.id === a.id);
            return existing ? { ...a, isSelected: existing.isSelected } : a;
          });
        });
        setRoomAmenities(roomList);
      } catch (error) {
        toast.error("Failed to load amenities");
      }
    };

    fetchAmenities();
  }, []);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const data = await getVenues();

        const formatted = data
          .filter((venue) => venue.isActive)
          .map((venue) => ({
            label: venue.venueName,
            value: String(venue.venueId),
          }));

        setVenueOptions(formatted);
      } catch (error) {
        toast.error("Failed to load venues");
      }
    };

    fetchVenues();
  }, []);

  useEffect(() => {
    const fetchPropertyTypes = async () => {
      try {
        const data = await getPropertyTypes();

        const formatted = data
          .filter((type) => type.isActive)
          .map((type) => ({
            label: type.name.trim(),
            value: String(type.id),
          }));

        setPropertyTypeOptions(formatted);
      } catch (error) {
        toast.error("Failed to load property types");
      }
    };

    fetchPropertyTypes();
  }, []);

  useEffect(() => {
    const fetchPropertyGrades = async () => {
      try {
        const data = await getPropertyGrades();

        const formatted = data
          .filter((grade) => grade.isActive)
          .map((grade) => ({
            label: grade.name.trim(),
            value: String(grade.id),
          }));

        setPropertyGradeOptions(formatted);
      } catch (error) {
        toast.error("Failed to load property grades");
      }
    };

    fetchPropertyGrades();
  }, []);

  // Property form data
  const [propertyData, setPropertyData] = useState({
    venueName: "",
    propertyName: "",
    propertyCode: "",
    address: "",
    primaryContact: "",
    primaryEmail: "",
    primaryPhone: "",
    propertyType: "",
    propertyGrade: "",
    numberOfRooms: 0,
    numberOfFloors: 1,
    isLiftAvailable: false,
    isHousekeepingAvailable: false,
    hasGroundFloor: true,
    hasBasement: false,
    checkInTime: "08:00",
    checkOutTime: "12:00",
    earlyCheckInHours: "06:00",
    lateCheckInHours: "13:00",
    lateCheckOutHours: "16:00",
    propertyAvailable: true,
    childAgeMax: 0,
  });

  const [contactInfoList, setContactInfoList] = useState<ContactInfo[]>([]);
  const [, setContracts] = useState<Contract[]>([]);
  const [, setCurrentContract] = useState<Contract>({
    id: "",
    fromDate: "",
    toDate: "",
    rentAmount: 0,
    roomTypeIds: [""],
    amenities: [],
    status: "active",
  });
  const [propertyAmenities, setPropertyAmenities] = useState<PropertyAmenity[]>(
    [],
  );
  const [roomAmenities, setRoomAmenities] = useState<PropertyAmenity[]>([]);
  const [, setRooms] = useState<RoomDetail[]>([]);
  const [venueOptions, setVenueOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [currencyOptions, setCurrencyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [propertyTypeOptions, setPropertyTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [propertyGradeOptions, setPropertyGradeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [roomTypeConfigs, setRoomTypeConfigs] = useState<ContractRoomType[]>(
    [],
  );
  const isEditMode = !!editingProperty;

  const [hotelContract, setHotelContract] = useState<HotelContractDetails>(
    DEFAULT_HOTEL_CONTRACT,
  );
  const [hotelContracts, setHotelContracts] = useState<AddedHotelContract[]>(
    [],
  );
  const [selectedHotelContract, setSelectedHotelContract] =
    useState<AddedHotelContract | null>(null);
  const [editingHotelContractId, setEditingHotelContractId] = useState<
    string | null
  >(null);
  const [hotelContractAccordionValue, setHotelContractAccordionValue] =
    useState<string>("");

  const [floorTypeOptions, setFloorTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [loading, setLoading] = useState(false);

  const getRoomTypeName = (id: string) => {
    return roomTypeOptions.find((r) => r.value === id)?.label || id;
  };

  const selectedPropertyTypeLabel =
    propertyTypeOptions.find(
      (option) => option.value === propertyData.propertyType,
    )?.label || "";

  const isHotelProperty =
    selectedPropertyTypeLabel.trim().toLowerCase() === "hotel";

  const hotelRoomRentTotal = roomTypeConfigs.reduce(
    (sum, room) => sum + Number(room.pricePerRoom) * Number(room.numberOfRooms),
    0,
  );

  const [, setDeletedHotelContractIds] = useState<number[]>([]);

  const hotelTaxAmount =
    hotelRoomRentTotal * (Number(hotelContract.taxPercentage || 0) / 100);

  const hotelContractGrandTotal =
    hotelRoomRentTotal +
    hotelTaxAmount +
    Number(hotelContract.securityDeposit || 0);

  const handleAgreementDocumentUpload = (file?: File) => {
    if (!file) return;
    const fileUrl = URL.createObjectURL(file);
    setHotelContract((prev) => {
      if (prev.agreementDocumentUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(prev.agreementDocumentUrl);
      }
      return {
        ...prev,
        agreementDocumentName: file.name,
        agreementDocumentUrl: fileUrl,
        agreementDocumentType: file.type,
        agreementDocumentSize: file.size,
      };
    });
  };

  const handleViewAgreementDocument = () => {
    if (!hotelContract.agreementDocumentUrl) {
      toast.error("Please upload an agreement document first");
      return;
    }
    window.open(
      hotelContract.agreementDocumentUrl,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const createHotelContractSnapshot = (): AddedHotelContract => ({
    ...hotelContract,
    hotelContractId: hotelContract.hotelContractId,
    publicId: hotelContract.publicId,

    // id: editingHotelContractId || `hotel-contract-${Date.now()}`,
    id: String(
      hotelContract.hotelContractId ?? editingHotelContractId ?? Date.now(),
    ),
    roomRentTotal: hotelRoomRentTotal,
    taxAmount: hotelTaxAmount,
    grandTotal: hotelContractGrandTotal,
    roomConfigs: roomTypeConfigs.map((config) => ({
      ...config,
      roomTypeIds: [...config.roomTypeIds],
      roomNames: [...(config.roomNames || [])],
      personTypes: [...config.personTypes],
      amenities: config.amenities?.map((amenity) => ({ ...amenity })),
    })),
    roomTypeRents: roomTypeConfigs.map((config) => ({
      roomTypeId: Number(config.roomTypeIds[0]),
      roomTypeName: getRoomTypeName(config.roomTypeIds[0]),
      numberOfRooms: Number(config.numberOfRooms || 0),
      rentPerRoom: Number(config.pricePerRoom || 0),
      totalRent:
        Number(config.pricePerRoom || 0) * Number(config.numberOfRooms || 0),
    })),
  });

  const resetHotelContractForm = () => {
    setHotelContract(DEFAULT_HOTEL_CONTRACT);
    setRoomTypeConfigs([]);
    setEditingHotelContractId(null);
  };

  const handleAddHotelContract = () => {
    if (!hotelContract.contractReferenceNo.trim()) {
      toast.error("Enter contract reference number");
      return;
    }

    const contractReference = hotelContract.contractReferenceNo.trim();

    if (contractReference.length < 3 || contractReference.length > 50) {
      toast.error("Contract Reference No. must be between 3 and 50 characters");
      return;
    }

    const contractReferenceRegex = /^[A-Za-z0-9_/-]+$/;

    if (!contractReferenceRegex.test(contractReference)) {
      toast.error(
        "Contract Reference No. can contain only letters, numbers, hyphen (-), underscore (_), and forward slash (/)",
      );
      return;
    }

    if (!hotelContract.contractStartDate || !hotelContract.contractEndDate) {
      toast.error("Select contract start and end dates");
      return;
    }

    const today = new Date().toISOString().split("T")[0];

    if (hotelContract.contractStartDate < today) {
      toast.error("Contract Start Date cannot be in the past");
      return;
    }

    if (hotelContract.contractStartDate > hotelContract.contractEndDate) {
      toast.error(
        "Contract Start Date must be less than or equal to Contract End Date",
      );
      return;
    }

    if (roomTypeConfigs.length === 0) {
      if (isHotelProperty && hotelContracts.length === 0) {
        toast.error("Add room type configuration before adding contract");
        return;
      }
    }

    const snapshot = createHotelContractSnapshot();

    setHotelContracts((prev) =>
      editingHotelContractId
        ? prev.map((contract) =>
            String(contract.hotelContractId) === editingHotelContractId
              ? snapshot
              : contract,
          )
        : [...prev, snapshot],
    );
    toast.success(
      editingHotelContractId
        ? "Contract updated successfully"
        : "Contract added successfully",
    );
    setEditingHotelContractId(null);
    resetHotelContractForm();
    setRoomTypeConfigs([]);
    setHotelContractAccordionValue("");
  };

  const handleEditHotelContract = (contract: AddedHotelContract) => {
    const id = String(contract.hotelContractId);
    setHotelContract({
      hotelContractId: contract.hotelContractId,
      publicId: contract.publicId,
      contractReferenceNo: contract.contractReferenceNo,
      contractStartDate: contract.contractStartDate,
      contractEndDate: contract.contractEndDate,
      currency: contract.currency,
      billingCycle: contract.billingCycle,
      paymentTerms: contract.paymentTerms,
      securityDeposit: contract.securityDeposit,
      taxPercentage: contract.taxPercentage,
      includedServices: contract.includedServices,
      cancellationPolicy: contract.cancellationPolicy,
      noticePeriodDays: contract.noticePeriodDays,
      agreementDocumentName: contract.agreementDocumentName,
      agreementDocumentUrl: contract.agreementDocumentUrl,
      agreementDocumentType: contract.agreementDocumentType,
      agreementDocumentSize: contract.agreementDocumentSize,
    });
    setRoomTypeConfigs(
      (contract.roomConfigs || []).map((config) => ({
        ...config,
        roomTypeIds: [...config.roomTypeIds],
        roomNames: [...(config.roomNames || [])],
        personTypes: [...config.personTypes],
        amenities: config.amenities?.map((amenity) => ({ ...amenity })),
      })),
    );
    setEditingHotelContractId(id);
    setSelectedHotelContract(null);
    setHotelContractAccordionValue("hotel-contract-details");
    toast.info("Contract loaded for editing");
  };

  const handleRemoveHotelContract = (id: string) => {
    const contract = hotelContracts.find((c) => c.id === id);

    if (contract?.hotelContractId) {
      setDeletedHotelContractIds((prev) => [
        ...prev,
        Number(contract.hotelContractId),
      ]);
    }

    setHotelContracts((prev) => prev.filter((c) => c.id !== id));

    toast.success("Contract removed");
  };

  const handleViewContractAgreement = (contract: AddedHotelContract) => {
    if (!contract.agreementDocumentUrl) {
      toast.error("No agreement document uploaded for this contract");
      return;
    }
    window.open(contract.agreementDocumentUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (
      editingProperty &&
      venueOptions.length > 0 &&
      propertyTypeOptions.length > 0 &&
      propertyGradeOptions.length > 0 &&
      roomTypeOptions.length > 0 &&
      floorTypeOptions.length > 0
    ) {
      setPropertyData({
        venueName: String(editingProperty.venueId || ""),
        propertyName: editingProperty.propertyName || "",
        propertyCode: editingProperty.propertyCode || "",
        address: editingProperty.address || "",
        primaryContact: editingProperty.primaryContact || "",
        primaryEmail: editingProperty.primaryEmail || "",
        primaryPhone: editingProperty.phoneNumber || "",
        propertyType: String(editingProperty.propertyTypeId || ""),
        propertyGrade: String(editingProperty.propertyGradeId || ""),
        numberOfRooms: editingProperty.totalRooms || 0,
        numberOfFloors: editingProperty.totalFloors || 1,
        isLiftAvailable: editingProperty.isLiftAvailable || false,
        isHousekeepingAvailable:
          editingProperty.isHousekeepingAvailable || false,
        hasGroundFloor: editingProperty.hasGroundFloor ?? true,
        hasBasement: editingProperty.hasBasement || false,
        checkInTime: editingProperty?.checkInTime?.slice(0, 5) || "14:00",
        checkOutTime: editingProperty?.checkOutTime?.slice(0, 5) || "12:00",
        earlyCheckInHours: editingProperty?.earlyCheckInTime?.slice(0, 5) || "",

        lateCheckInHours: editingProperty?.lateCheckInTime?.slice(0, 5) || "",

        lateCheckOutHours:
          editingProperty?.extraLateCheckOutTime?.slice(0, 5) || "",
        propertyAvailable: editingProperty.isActive ?? true,
        childAgeMax: editingProperty?.childAgeMax ?? 12,
      });

      if (editingProperty.hotelContracts?.length > 0) {
        const mappedContracts = editingProperty.hotelContracts.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (contract: any) => {
            const roomRentTotal =
              contract.roomTypeRents?.reduce(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (sum: number, room: any) => sum + Number(room.totalRent || 0),
                0,
              ) || 0;

            const taxAmount =
              roomRentTotal * (Number(contract.taxPercentage || 0) / 100);

            return {
              ...contract,

              id: String(contract.hotelContractId),

              hotelContractId: contract.hotelContractId,
              publicId: contract.publicId,

              taxPercentage: contract.taxPercentage,

              roomRentTotal,

              taxAmount,

              grandTotal:
                roomRentTotal +
                taxAmount +
                Number(contract.securityDeposit || 0),

              roomConfigs:
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                contract.roomTypeRents?.map((room: any) => {
                  const propertyRoom = editingProperty.roomTypes.find(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (r: any) => r.roomTypeId === room.roomTypeId,
                  );

                  return {
                    id: String(room.hotelContractRoomTypeRentId),

                    roomTypeIds: [String(room.roomTypeId)],

                    propertyRoomTypeId: propertyRoom?.propertyRoomTypeId,

                    pricePerRoom: Number(room.rentPerRoom),

                    numberOfRooms: Number(room.numberOfRooms),

                    maxAdults: propertyRoom?.maxAdults ?? 0,

                    maxChildren: propertyRoom?.maxChildren ?? 0,

                    maxOccupancy: propertyRoom?.maxOccupancy ?? 0,

                    minAdults: propertyRoom?.minAdults ?? 0,

                    minOccupancy: propertyRoom?.minOccupancy ?? 0,

                    childAgeMax: propertyRoom?.childAgeMax ?? 0,

                    roomNames:
                      propertyRoom?.roomNamesArray ??
                      propertyRoom?.roomNames?.split(",") ??
                      [],

                    personTypes: [],

                    amenities: roomAmenities.map((a) => ({
                      ...a,
                      isSelected:
                        propertyRoom?.amenityIds?.includes(a.id) || false,
                    })),
                  };
                }) ?? [],
            };
          },
        );

        setHotelContracts(mappedContracts);

        setHotelContract(DEFAULT_HOTEL_CONTRACT);
        setEditingHotelContractId(null);
        setRoomTypeConfigs([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editingProperty,
    venueOptions,
    propertyTypeOptions,
    propertyGradeOptions,
    roomTypeOptions,
    floorTypeOptions,
  ]);

  // Room Type Configuration for Configuration tab

  const [currentRoomTypeConfig, setCurrentRoomTypeConfig] =
    useState<ContractRoomType>({
      id: "",
      roomTypeIds: [""],
      pricePerRoom: 0,
      maxOccupancy: 3,
      maxAdults: 2,
      maxChildren: 1,
      minAdults: 1,
      minOccupancy: 1,
      childAgeMax: 12,
      numberOfRooms: 1,
      roomNames: [],
      personTypes: [],
      amenities: [],
    });
  const [isEditingRoomTypeConfig, setIsEditingRoomTypeConfig] = useState(false);
  const [isRoomTypeDialogOpen, setIsRoomTypeDialogOpen] = useState(false);

  const [errors, setErrors] = useState({
    venueName: "",
    propertyName: "",
    propertyCode: "",
    address: "",
    primaryContact: "",
    primaryEmail: "",
    primaryPhone: "",
    propertyType: "",
    propertyGrade: "",
    checkInTime: "",
    checkOutTime: "",
  });

  const [contactErrors, setContactErrors] = useState<{
    [key: string]: Partial<ContactInfo>;
  }>({});

  // Add new contact info
  const handleAddContactInfo = () => {
    const newContact: ContactInfo = {
      id: `contact-${Date.now()}`,
      name: "",
      label: "",
      email: "",
      phone: "",
    };
    setContactInfoList([...contactInfoList, newContact]);
  };

  // Remove contact info
  const handleRemoveContactInfo = (id: string) => {
    setContactInfoList(contactInfoList.filter((c) => c.id !== id));
  };

  // Update contact info
  const handleUpdateContactInfo = (
    id: string,
    field: keyof ContactInfo,
    value: string,
  ) => {
    setContactInfoList(
      contactInfoList.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );
  };

  useEffect(() => {
    console.log("editingHotelContractId changed:", editingHotelContractId);
  }, [editingHotelContractId]);

  // Auto-calculate total rent amount when room types change
  useEffect(() => {
    const totalRent = roomTypeConfigs.reduce((sum, rt) => {
      return sum + rt.pricePerRoom * rt.numberOfRooms;
    }, 0);

    setCurrentContract((prev) => ({
      ...prev,
      rentAmount: totalRent,
    }));
  }, [roomTypeConfigs]);

  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const data = await getRoomTypes();
        const formatted = data
          .filter((room) => room.isActive)
          .map((room) => ({
            label: room.roomName,
            value: room.id.toString(),
          }));
        setRoomTypeOptions(formatted);
      } catch (error) {
        toast.error("Failed to load room types");
      }
    };
    fetchRoomTypes();
  }, []);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const data = await getCurrencies();

        const formatted = data
          .filter((currency) => currency.isActive)
          .map((currency) => ({
            label: currency.currencyCode,
            value: currency.currencyCode,
          }));

        setCurrencyOptions(formatted);
      } catch (error) {
        toast.error("Failed to load currencies");
      }
    };

    fetchCurrencies();
  }, []);

  useEffect(() => {
    const fetchFloors = async () => {
      try {
        const data = await getFloors();
        const formatted = data.map((floor) => ({
          label: floor.floorName,
          value: floor.publicId,
        }));
        setFloorTypeOptions(formatted);
      } catch (error) {
        toast.error("Failed to load floors");
      }
    };
    fetchFloors();
  }, []);

  // Prefill property amenities after they load
  useEffect(() => {
    if (editingProperty && propertyAmenities.length > 0) {
      setPropertyAmenities(
        propertyAmenities.map((a) => ({
          ...a,
          isSelected: editingProperty.amenityIds?.includes(a.id) || false,
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingProperty, propertyAmenities.length]);

  // Prefill contacts
  useEffect(() => {
    if (editingProperty) {
      setContactInfoList(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        editingProperty.contacts?.map((c: any, index: number) => ({
          id: `contact-${index}`,
          name: c.name,
          label: c.label,
          email: c.email,
          phone: c.phone,
        })) || [],
      );
    }
  }, [editingProperty]);

  // Prefill room types configuration- ONLY RUNS AFTER ALL OPTIONS ARE LOADED
  useEffect(() => {
    if (
      editingProperty &&
      !isHotelProperty &&
      roomAmenities.length > 0 &&
      roomTypeOptions.length > 0 &&
      floorTypeOptions.length > 0
    ) {
      const mappedRoomTypes =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        editingProperty.roomTypes?.map((rt: any, index: number) => ({
          id: `room-${index}`,
          roomTypeIds: [String(rt.roomTypeId)],
          numberOfRooms: Number(rt.numberOfRooms),
          roomNames: Array.isArray(rt.roomNamesArray)
            ? [...rt.roomNamesArray]
            : typeof rt.roomNames === "string"
              ? rt.roomNames
                  .split(",")
                  .map((x: string) => x.trim())
                  .filter(Boolean)
              : [],
          minAdults: rt.minAdults || 1,
          minOccupancy: rt.minOccupancy || 1,
          maxAdults: rt.maxAdults,
          maxChildren: rt.maxChildren,
          maxOccupancy: rt.maxOccupancy,
          childAgeMax: rt.childAgeMax ?? 12,
          pricePerRoom: 0,
          personTypes: [],
          amenities: roomAmenities.map((a) => ({
            ...a,
            isSelected: rt.amenityIds?.includes(a.id),
          })),
        })) || [];

      if (!isHotelProperty) {
        setRoomTypeConfigs(mappedRoomTypes);
      }

      if (mappedRoomTypes.length > 0) {
        setCurrentRoomTypeConfig({
          ...mappedRoomTypes[0],

          roomNames: mappedRoomTypes[0].roomNames?.length
            ? [...mappedRoomTypes[0].roomNames]
            : Array(mappedRoomTypes[0].numberOfRooms).fill(""),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingProperty, roomAmenities, roomTypeOptions, floorTypeOptions]);

  // Helper to get amenities for a room type
  const getRoomTypeAmenities = (config: ContractRoomType): string[] => {
    return (
      config.amenities?.filter((a) => a.isSelected).map((a) => a.name) || []
    );
  };

  const getPropertyAmenities = (): string[] => {
    return propertyAmenities.filter((a) => a.isSelected).map((a) => a.name);
  };

  // Room Type Configuration
  const getAvailableRoomTypeOptions = () => {
    const usedRoomTypeIds = new Set(
      roomTypeConfigs
        .filter(
          (config) =>
            !isEditingRoomTypeConfig || config.id !== currentRoomTypeConfig.id,
        )
        .flatMap((config) => config.roomTypeIds)
        .filter(Boolean),
    );

    return roomTypeOptions.filter(
      (option) => !usedRoomTypeIds.has(option.value.toString()),
    );
  };

  const getUsedRoomNames = () => {
    return roomTypeConfigs
      .filter(
        (config) =>
          !isEditingRoomTypeConfig || config.id !== currentRoomTypeConfig.id,
      )
      .flatMap((config) => config.roomNames || [])
      .map((name) => String(name).trim().toLowerCase())
      .filter(Boolean);
  };

  // Room Type Configuration handlers for Configuration tab
  const handleAddRoomTypeConfig = () => {
    if (
      currentRoomTypeConfig.roomTypeIds.length === 0 ||
      !currentRoomTypeConfig.roomTypeIds[0]
    ) {
      toast.error("Please select a room type");
      return;
    }
    const selectedRoomTypeId = currentRoomTypeConfig.roomTypeIds[0];

    const roomTypeAlreadyExists = roomTypeConfigs.some(
      (config) =>
        config.id !== currentRoomTypeConfig.id &&
        config.roomTypeIds[0] === selectedRoomTypeId,
    );

    if (roomTypeAlreadyExists) {
      const roomTypeName = getRoomTypeName(selectedRoomTypeId);

      toast.error(
        `Room type "${roomTypeName}" is already used for this property`,
      );
      return;
    }
    if (
      currentRoomTypeConfig.numberOfRooms < 1 ||
      currentRoomTypeConfig.numberOfRooms > 200
    ) {
      toast.error("Number of rooms must be between 1 and 200");
      return;
    }
    if (
      currentRoomTypeConfig.childAgeMax < 1 ||
      currentRoomTypeConfig.childAgeMax > 200
    ) {
      toast.error("Child Age Max must be between 1 and 200");
      return;
    }
    if (
      currentRoomTypeConfig.minAdults < 1 ||
      currentRoomTypeConfig.minAdults > 200
    ) {
      toast.error("Min. Adults must be between 1 and 200");
      return;
    }
    if (
      currentRoomTypeConfig.maxAdults < 1 ||
      currentRoomTypeConfig.maxAdults > 200
    ) {
      toast.error("Max Adults must be between 1 and 200");
      return;
    }
    if (
      currentRoomTypeConfig.maxChildren < 1 ||
      currentRoomTypeConfig.maxChildren > 200
    ) {
      toast.error("Max Children must be between 1 and 200");
      return;
    }
    if (
      currentRoomTypeConfig.minOccupancy < 1 ||
      currentRoomTypeConfig.minOccupancy > 200
    ) {
      toast.error("Min. Occupancy must be between 1 and 200");
      return;
    }
    if (
      currentRoomTypeConfig.maxOccupancy < 1 ||
      currentRoomTypeConfig.maxOccupancy > 200
    ) {
      toast.error("Max Occupancy must be between 1 and 200");
      return;
    }

    // Validate room names
    const validRoomNames = (currentRoomTypeConfig.roomNames || []).map((name) =>
      String(name).trim(),
    );

    // Make sure all room names are entered
    if (
      validRoomNames.length !== currentRoomTypeConfig.numberOfRooms ||
      validRoomNames.some((name) => !name)
    ) {
      toast.error("Please enter all room names");
      return;
    }

    // Check duplicate room names inside the current room type
    const normalizedCurrentRoomNames = validRoomNames.map((name) =>
      name.toLowerCase(),
    );

    const duplicateRoomNames = normalizedCurrentRoomNames.filter(
      (name, index, array) => array.indexOf(name) !== index,
    );

    if (duplicateRoomNames.length > 0) {
      toast.error(
        `Duplicate room name "${duplicateRoomNames[0]}" is not allowed`,
      );
      return;
    }

    // Check ALL room names already used by another room type
    const usedRoomNames = getUsedRoomNames();

    const usedRoomNameSet = new Set(usedRoomNames);

    const alreadyUsedRoomNames = validRoomNames.filter((name) =>
      usedRoomNameSet.has(name.toLowerCase()),
    );

    // Remove duplicates while preserving the original room-name 
    const uniqueAlreadyUsedRoomNames = Array.from(
      new Map(
        alreadyUsedRoomNames.map((name) => [name.toLowerCase(), name]),
      ).values(),
    );

    if (uniqueAlreadyUsedRoomNames.length > 0) {
      const roomNamesText = uniqueAlreadyUsedRoomNames
        .map((name) => `"${name}"`)
        .join(", ");

      toast.error(
        `Room name${uniqueAlreadyUsedRoomNames.length > 1 ? "s" : ""} ${roomNamesText} ${
          uniqueAlreadyUsedRoomNames.length > 1 ? "are" : "is"
        } already used by another room type`,
      );

      return;
    }

    if (isEditingRoomTypeConfig) {
      // Update existing configuration
      const updatedConfig = {
        ...currentRoomTypeConfig,
        roomNames: [...validRoomNames],
        amenities: roomAmenities.map((a) => {
          const existing = currentRoomTypeConfig.amenities?.find(
            (ca) => ca.id === a.id,
          );
          return existing
            ? { ...existing }
            : {
                ...a,
                isSelected: false,
              };
        }),
      };
      const updatedRoomConfigs = roomTypeConfigs.map((config) =>
        config.id === currentRoomTypeConfig.id ? updatedConfig : config,
      );

      setRoomTypeConfigs(updatedRoomConfigs);

      if (editingHotelContractId) {
        const snapshot = createHotelContractSnapshot();
        snapshot.roomConfigs = updatedRoomConfigs;

        snapshot.roomTypeRents = updatedRoomConfigs.map((room) => ({
          roomTypeId: Number(room.roomTypeIds[0]),
          roomTypeName: getRoomTypeName(room.roomTypeIds[0]),
          numberOfRooms: Number(room.numberOfRooms),
          rentPerRoom: Number(room.pricePerRoom),
          totalRent: Number(room.numberOfRooms) * Number(room.pricePerRoom),
        }));

        setHotelContracts((prev) =>
          prev.map((contract) =>
            String(contract.hotelContractId) === editingHotelContractId
              ? snapshot
              : contract,
          ),
        );
      }

      toast.success("Room type configuration updated successfully");
      setIsRoomTypeDialogOpen(false);

      setIsEditingRoomTypeConfig(false);
      setCurrentRoomTypeConfig({
        id: "",
        roomTypeIds: [""],
        pricePerRoom: 0,
        maxOccupancy: 2,
        maxAdults: 2,
        maxChildren: 0,
        minAdults: 1,
        minOccupancy: 1,
        childAgeMax: 12,
        numberOfRooms: 1,
        roomNames: [],
        personTypes: [],
        amenities: [],
      });
      return;
    } else {
      const newConfig: ContractRoomType = {
        ...currentRoomTypeConfig,
        roomNames: validRoomNames,
        amenities: roomAmenities.map((a) => {
          const existing = currentRoomTypeConfig.amenities?.find(
            (ca) => ca.id === a.id,
          );
          return existing
            ? { ...existing }
            : {
                ...a,
                isSelected: false,
              };
        }),
        id: `room-config-${Date.now()}`,
      };
      setRoomTypeConfigs((prev) => [
        ...prev,
        {
          ...newConfig,
          pricePerRoom: newConfig.pricePerRoom,
        },
      ]);
      toast.success("Room type configuration added successfully");
      setIsRoomTypeDialogOpen(false);
    }

    // Reset form
    setCurrentRoomTypeConfig({
      id: "",
      roomTypeIds: [""],
      pricePerRoom: 0,
      maxOccupancy: 3,
      maxAdults: 2,
      maxChildren: 1,
      minAdults: 1,
      minOccupancy: 1,
      childAgeMax: 12,
      numberOfRooms: 1,
      roomNames: [],
      personTypes: [],
      amenities: [],
    });
  };

  useEffect(() => {
    if (
      roomAmenities.length > 0 &&
      !isEditingRoomTypeConfig &&
      currentRoomTypeConfig.id === "" &&
      currentRoomTypeConfig.amenities?.length === 0 &&
      !editingProperty
    ) {
      setCurrentRoomTypeConfig((prev) => ({
        ...prev,

        amenities: roomAmenities.map((a) => ({
          ...a,
          isSelected: false,
        })),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    roomAmenities,
    isEditingRoomTypeConfig,
    currentRoomTypeConfig.id,
    editingProperty,
  ]);

  const handleRemoveRoomTypeConfig = (id: string) => {
    setRoomTypeConfigs(roomTypeConfigs.filter((config) => config.id !== id));
    toast.success("Room type configuration removed");
  };

  const handleEditRoomTypeConfig = (
    config: ContractRoomType,
    contractId?: string,
  ) => {
    setCurrentRoomTypeConfig({
      ...config,
      roomTypeIds: [...config.roomTypeIds],
      roomNames: [...(config.roomNames || [])],
      amenities: roomAmenities.map((a) => {
        const existing = config.amenities?.find((ca) => ca.id === a.id);
        return existing ? { ...existing } : { ...a, isSelected: false };
      }),
    });
    setIsEditingRoomTypeConfig(true);
    if (contractId) {
      setEditingHotelContractId(contractId);
    }
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setIsRoomTypeDialogOpen(true);

    toast.info('Edit the configuration and click "Update Configuration"');
  };

  const validateForm = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newErrors: any = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newContactErrors: any = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+\d{11,14}$/;
    if (!propertyData.venueName) newErrors.venueName = "Venue name is required";
    if (!propertyData.propertyName.trim()) {
      newErrors.propertyName = "Property Name is required";
    } else if (propertyData.propertyName.length > 50) {
      newErrors.propertyName = "Property Name cannot exceed 50 characters";
    }
    if (!propertyData.propertyCode.trim()) {
      newErrors.propertyCode = "Property Code is required";
    } else if (propertyData.propertyCode.length > 20) {
      newErrors.propertyCode = "Property Code cannot exceed 20 characters";
    }
    if (!propertyData.address.trim()) {
      newErrors.address = "Address is required";
    } else if (propertyData.address.length > 50) {
      newErrors.address = "Address cannot exceed 50 characters";
    }
    if (!propertyData.primaryContact.trim()) {
      newErrors.primaryContact = "Primary Contact is required";
    } else if (propertyData.primaryContact.length > 50) {
      newErrors.primaryContact = "Primary Contact cannot exceed 50 characters";
    }
    if (!propertyData.primaryEmail) {
      newErrors.primaryEmail = "Email is required";
    } else if (!emailRegex.test(propertyData.primaryEmail)) {
      newErrors.primaryEmail = "Enter a valid email address";
    }
    if (!propertyData.primaryPhone) {
      newErrors.primaryPhone = "Phone number is required";
    } else if (!phoneRegex.test(propertyData.primaryPhone)) {
      newErrors.primaryPhone =
        "Enter phone number with country code (e.g. +91 9876543210)";
    }
    if (!propertyData.propertyType)
      newErrors.propertyType = "Property type is required";
    if (!propertyData.propertyGrade)
      newErrors.propertyGrade = "Property grade is required";
    if (!propertyData.checkInTime)
      newErrors.checkInTime = "Check-in time is required";
    if (!propertyData.checkOutTime)
      newErrors.checkOutTime = "Check-out time is required";

    contactInfoList.forEach((contact) => {
      const contactError: Partial<ContactInfo> = {};
      if (!contact.name?.trim()) {
        contactError.name = "Name is required";
      }
      if (!contact.label?.trim()) {
        contactError.label = "Label is required";
      }
      if (!contact.email?.trim()) {
        contactError.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(contact.email)) {
        contactError.email = "Invalid email format";
      }
      if (!contact.phone?.trim()) {
        contactError.phone = "Phone is required";
      }
      if (Object.keys(contactError).length > 0) {
        newContactErrors[contact.id] = contactError;
      }
    });

    setErrors(newErrors);
    setContactErrors(newContactErrors);
    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(newContactErrors).length === 0
    );
  };

  const handleSaveProperty = async () => {
    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }
    if (isHotelProperty) {
      if (hotelContracts.length === 0) {
        toast.error("Add at least one hotel contract");
        setActiveTab("configuration");
        return;
      }
    } else {
      if (roomTypeConfigs.length === 0) {
        toast.error("Add at least one room configuration");
        setActiveTab("configuration");
        return;
      }
    }
    let finalConfigs = isHotelProperty
      ? hotelContracts.flatMap((contract) => contract.roomConfigs)
      : [...roomTypeConfigs];
    if (isEditingRoomTypeConfig) {
      finalConfigs = finalConfigs.map((config) =>
        config.id === currentRoomTypeConfig.id
          ? {
              ...currentRoomTypeConfig,
              roomNames:
                currentRoomTypeConfig.roomNames?.map((name) =>
                  String(name).trim(),
                ) || [],
            }
          : config,
      );
    }

    const latestConfigs = finalConfigs.map((config) => ({
      ...config,
      propertyRoomTypeId: config.propertyRoomTypeId,
      numberOfRooms: Number(config.numberOfRooms),
      roomNames: (config.roomNames || []).map((name) => String(name).trim()),
    }));

    try {
      const payload = {
        venueId: Number(propertyData.venueName),

        propertyName: propertyData.propertyName,
        propertyCode: propertyData.propertyCode,
        address: propertyData.address,

        primaryContact: propertyData.primaryContact,
        primaryEmail: propertyData.primaryEmail,
        phoneNumber: propertyData.primaryPhone,

        propertyTypeId: Number(propertyData.propertyType),
        propertyGradeId: Number(propertyData.propertyGrade),

        checkInTime:
          propertyData.checkInTime.length === 5
            ? propertyData.checkInTime + ":00"
            : propertyData.checkInTime,

        checkOutTime:
          propertyData.checkOutTime.length === 5
            ? propertyData.checkOutTime + ":00"
            : propertyData.checkOutTime,

        earlyCheckInTime:
          propertyData.earlyCheckInHours?.length === 5
            ? propertyData.earlyCheckInHours + ":00"
            : propertyData.earlyCheckInHours,

        lateCheckInTime:
          propertyData.lateCheckInHours?.length === 5
            ? propertyData.lateCheckInHours + ":00"
            : propertyData.lateCheckInHours,

        extraLateCheckOutTime:
          propertyData.lateCheckOutHours?.length === 5
            ? propertyData.lateCheckOutHours + ":00"
            : propertyData.lateCheckOutHours,

        childAgeMax: propertyData.childAgeMax,

        contacts: contactInfoList.map((c) => ({
          name: c.name,
          label: c.label,
          email: c.email,
          phone: c.phone,
        })),

        amenityIds: propertyAmenities
          .filter((a) => a.isSelected)
          .map((a) => Number(a.id)),

        roomTypes: latestConfigs.map((config) => ({
          roomTypeId: Number(config.roomTypeIds[0]),

          numberOfRooms: Number(config.numberOfRooms),

          maxAdults: Number(config.maxAdults),
          maxChildren: Number(config.maxChildren),
          maxOccupancy: Number(config.maxOccupancy),

          childAgeMax: Number(config.childAgeMax),

          minAdults: Number(config.minAdults),
          minOccupancy: Number(config.minOccupancy),

          rentPerRoom: Number(config.pricePerRoom),

          roomNames: config.roomNames ?? [],

          amenityIds:
            config.amenities?.filter((a) => a.isSelected).map((a) => a.id) ??
            [],
        })),

        hotelContracts: isHotelProperty
          ? hotelContracts.map((contract) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const payload: any = {
                contractReferenceNo: contract.contractReferenceNo,
                contractStartDate: contract.contractStartDate,
                contractEndDate: contract.contractEndDate,

                currency: contract.currency,
                billingCycle: contract.billingCycle,
                paymentTerms: contract.paymentTerms,

                noticePeriodDays: contract.noticePeriodDays,
                securityDeposit: contract.securityDeposit,
                taxPercentage: contract.taxPercentage,

                includedServices: contract.includedServices,
                cancellationPolicy: contract.cancellationPolicy,
                agreementDocumentUrl: contract.agreementDocumentUrl,

                roomTypeRents: contract.roomConfigs.map((room) => ({
                  roomTypeId: Number(room.roomTypeIds[0]),
                  rentPerRoom: Number(room.pricePerRoom),
                })),
              };

              // ONLY for existing contracts while editing
              if (
                editingProperty?.publicId &&
                contract.hotelContractId &&
                contract.publicId
              ) {
                payload.hotelContractId = contract.hotelContractId;
                payload.publicId = contract.publicId;
              }

              return payload;
            })
          : [],

        // deletedHotelContractIds: deletedHotelContractIds,
      };

      let res;
      if (editingProperty?.publicId) {
        console.log("hotelContracts state", hotelContracts);

        console.log(
          "Payload Hotel Contracts",
          JSON.stringify(payload.hotelContracts, null, 2),
        );
        hotelContracts.forEach((c, i) => {
          console.log("Contract", i);
          console.log("hotelContractId", c.hotelContractId);
          console.log("roomConfigs", c.roomConfigs);
          console.log("roomTypeRents", c.roomTypeRents);
        });
        res = await updateProperty(editingProperty.publicId, payload);
        toast.success("Property updated successfully");
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        res = await createProperty(payload);
        toast.success("Property created successfully");
      }

      navigate("/property-details");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to create property";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleSaveProperty}
            style={{ background: "#5B9BD5" }}
            className="text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {editingProperty ? "Update Property" : "Save Property"}
          </Button>
        </div>

        {/* Tabs */}
        <Card className="bg-white border-[#5B9BD5]/30">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="w-full grid grid-cols-2 h-auto p-2 bg-gradient-to-r from-sky-50 to-blue-50">
              <TabsTrigger
                value="basic"
                className="data-[state=active]:bg-white data-[state=active]:text-[#5B9BD5] data-[state=active]:shadow-sm py-3"
              >
                <User className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Basic Information</span>
                <span className="sm:hidden">Basic</span>
              </TabsTrigger>
              <TabsTrigger
                value="configuration"
                className="data-[state=active]:bg-white data-[state=active]:text-[#5B9BD5] data-[state=active]:shadow-sm py-3"
              >
                <Settings className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Configuration</span>
                <span className="sm:hidden">Config</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Basic Information */}
            <TabsContent value="basic" className="mt-0">
              <CardContent className="p-6 space-y-6">
                {/* Basic Property Information */}
                <div className="space-y-4">
                  <div className="border-b pb-2">
                    <h3
                      className="text-base font-semibold"
                      style={{ color: "#5B9BD5" }}
                    >
                      Basic Property Information
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <SelectFieldset label="Venue Name" required>
                        <Select
                          value={propertyData.venueName}
                          onValueChange={(value) => {
                            setPropertyData({
                              ...propertyData,
                              venueName: value,
                            });
                            setErrors({ ...errors, venueName: "" });
                          }}
                        >
                          <SelectTriggerUI
                            className={errors.venueName ? "border-red-500" : ""}
                          >
                            <SelectValue placeholder="Select venue" />
                          </SelectTriggerUI>
                          <SelectContent>
                            {venueOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </SelectFieldset>
                      {errors.venueName && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.venueName}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Property Name"
                        required
                        id="propertyName"
                        value={propertyData.propertyName}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^A-Za-z0-9 ]/g, "")
                            .slice(0, 50);
                          setPropertyData({
                            ...propertyData,
                            // propertyName: e.target.value,
                            propertyName: value,
                          });
                          setErrors({ ...errors, propertyName: "" });
                        }}
                        placeholder="e.g., Qasr Al Dur 1447"
                        className={errors.propertyName ? "border-red-500" : ""}
                      />

                      {errors.propertyName && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.propertyName}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Property Code"
                        required
                        id="propertyCode"
                        value={propertyData.propertyCode}
                        onChange={(e) => {
                          setPropertyData({
                            ...propertyData,
                            propertyCode: e.target.value
                              .replace(/[^A-Za-z0-9 -]/g, "")
                              .slice(0, 20),
                          });
                          setErrors({ ...errors, propertyCode: "" });
                        }}
                        placeholder="e.g., QAD-1447"
                        className={errors.propertyCode ? "border-red-500" : ""}
                      />
                      {errors.propertyCode && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.propertyCode}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <SelectFieldset label="Property Type" required>
                        <Select
                          value={propertyData.propertyType}
                          onValueChange={(value) => {
                            setPropertyData({
                              ...propertyData,
                              propertyType: value,
                            });
                            // Reset contracts and rooms when switching
                            setContracts([]);
                            setRooms([]);
                            setErrors({ ...errors, propertyType: "" });
                          }}
                        >
                          <SelectTriggerUI
                            className={
                              errors.propertyType ? "border-red-500" : ""
                            }
                          >
                            <SelectValue placeholder="Select type" />
                          </SelectTriggerUI>
                          <SelectContent>
                            {propertyTypeOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </SelectFieldset>
                      {errors.propertyType && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.propertyType}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <SelectFieldset label=" Property Grade" required>
                        <Select
                          value={propertyData.propertyGrade}
                          onValueChange={(value) => {
                            setPropertyData({
                              ...propertyData,
                              propertyGrade: value,
                            });
                            setErrors({ ...errors, propertyGrade: "" });
                          }}
                        >
                          <SelectTriggerUI
                            className={
                              errors.propertyGrade ? "border-red-500" : ""
                            }
                          >
                            <SelectValue placeholder="Select grade" />
                          </SelectTriggerUI>
                          <SelectContent>
                            {propertyGradeOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </SelectFieldset>
                      {errors.propertyGrade && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.propertyGrade}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Address"
                        required
                        id="address"
                        value={propertyData.address}
                        onChange={(e) => {
                          setPropertyData({
                            ...propertyData,
                            address: e.target.value
                              .replace(/[^A-Za-z0-9 ]/g, "")
                              .slice(0, 50),
                          });
                          setErrors({ ...errors, address: "" });
                        }}
                        placeholder="Property address"
                        className={errors.address ? "border-red-500" : ""}
                      />
                      {errors.address && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.address}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Primary Contact Information */}
                <div className="space-y-4">
                  <div className="border-b pb-2">
                    <h3
                      className="text-base font-semibold"
                      style={{ color: "#5B9BD5" }}
                    >
                      Primary Contact Information
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <InputFieldset
                        label="Primary Contact"
                        required
                        id="primaryContact"
                        value={propertyData.primaryContact}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^A-Za-z ]/g, "")
                            .slice(0, 50);
                          setPropertyData({
                            ...propertyData,
                            primaryContact: value,
                          });
                          setErrors({ ...errors, primaryContact: "" });
                        }}
                        placeholder="Contact person name"
                        className={
                          errors.primaryContact ? "border-red-500" : ""
                        }
                      />
                      {errors.primaryContact && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.primaryContact}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Primary Email"
                        required
                        id="primaryEmail"
                        type="email"
                        value={propertyData.primaryEmail}
                        onChange={(e) => {
                          setPropertyData({
                            ...propertyData,
                            primaryEmail: e.target.value.replace(
                              /[^A-Za-z0-9@._-]/g,
                              "",
                            ),
                          });
                          setErrors({ ...errors, primaryEmail: "" });
                        }}
                        placeholder="email@example.com"
                        className={errors.primaryEmail ? "border-red-500" : ""}
                      />
                      {errors.primaryEmail && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.primaryEmail}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Phone Number"
                        required
                        id="primaryPhone"
                        type="tel"
                        value={propertyData.primaryPhone}
                        onChange={(e) => {
                          setPropertyData({
                            ...propertyData,
                            primaryPhone: e.target.value.replace(
                              /[^0-9+]/g,
                              "",
                            ),
                          });
                          setErrors({ ...errors, primaryPhone: "" });
                        }}
                        placeholder="+1234567890"
                        className={errors.primaryPhone ? "border-red-500" : ""}
                      />
                      {errors.primaryPhone && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.primaryPhone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional Contact Information */}
                <div className="space-y-4">
                  <div className="border-b pb-2 flex items-center justify-between">
                    <h3
                      className="text-base font-semibold"
                      style={{ color: "#5B9BD5" }}
                    >
                      Additional Contact Information
                    </h3>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddContactInfo}
                      className="h-8"
                      style={{ background: "#5B9BD5" }}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Contact
                    </Button>
                  </div>

                  {contactInfoList.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 border border-dashed rounded-lg">
                      <p className="text-sm">No additional contacts added</p>
                      <p className="text-xs mt-1">
                        Click "Add Contact" to add contact information
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contactInfoList.map((contact) => (
                        <Card key={contact.id} className="border-[#5B9BD5]/20">
                          <CardContent className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div className="space-y-2">
                                <InputFieldset
                                  label="Name"
                                  id={`name-${contact.id}`}
                                  value={contact.name}
                                  onChange={(e) => {
                                    const value = removeFullSpecialCharacters(
                                      e.target.value,
                                    );
                                    handleUpdateContactInfo(
                                      contact.id,
                                      "name",
                                      value,
                                    );
                                  }}
                                  className={
                                    contactErrors[contact.id]?.name
                                      ? "border-red-500"
                                      : ""
                                  }
                                  placeholder="Contact person name"
                                />
                                {contactErrors[contact.id]?.name && (
                                  <p className="text-red-500 text-xs">
                                    {contactErrors[contact.id].name}
                                  </p>
                                )}
                              </div>
                              <div className="space-y-2">
                                <InputFieldset
                                  label="Label / Department"
                                  id={`label-${contact.id}`}
                                  value={contact.label}
                                  onChange={(e) =>
                                    handleUpdateContactInfo(
                                      contact.id,
                                      "label",
                                      e.target.value,
                                    )
                                  }
                                  className={
                                    contactErrors[contact.id]?.label
                                      ? "border-red-500"
                                      : ""
                                  }
                                  placeholder="e.g., Accounts, Registration"
                                />
                                {contactErrors[contact.id]?.label && (
                                  <p className="text-red-500 text-xs">
                                    {contactErrors[contact.id].label}
                                  </p>
                                )}
                              </div>
                              <div className="space-y-2">
                                <InputFieldset
                                  label="Email"
                                  id={`email-${contact.id}`}
                                  type="email"
                                  value={contact.email}
                                  onChange={(e) =>
                                    handleUpdateContactInfo(
                                      contact.id,
                                      "email",
                                      e.target.value,
                                    )
                                  }
                                  className={
                                    contactErrors[contact.id]?.email
                                      ? "border-red-500"
                                      : ""
                                  }
                                  placeholder="email@example.com"
                                />
                                {contactErrors[contact.id]?.email && (
                                  <p className="text-red-500 text-xs">
                                    {contactErrors[contact.id].email}
                                  </p>
                                )}
                              </div>
                              <div className="space-y-2">
                                <div className="flex gap-2">
                                  <div>
                                    <InputFieldset
                                      label="Phone Number"
                                      id={`phone-${contact.id}`}
                                      value={contact.phone}
                                      onChange={(e) =>
                                        handleUpdateContactInfo(
                                          contact.id,
                                          "phone",
                                          e.target.value,
                                        )
                                      }
                                      className={`flex-1 ${
                                        contactErrors[contact.id]?.phone
                                          ? "border-red-500"
                                          : ""
                                      }`}
                                      placeholder="+1234567890"
                                    />
                                    {contactErrors[contact.id]?.phone && (
                                      <p className="text-red-500 text-xs">
                                        {contactErrors[contact.id].phone}
                                      </p>
                                    )}
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      handleRemoveContactInfo(contact.id)
                                    }
                                    className="text-red-600 hover:bg-red-50 h-10 w-10 p-0"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </TabsContent>

            {/* Tab 2: Property Configuration */}
            <TabsContent value="configuration" className="mt-0">
              <CardContent className="p-6 space-y-6">
                {/* Property Configuration */}
                <div className="space-y-4">
                  <div className="border-b pb-2">
                    <h3
                      className="text-base font-semibold"
                      style={{ color: "#5B9BD5" }}
                    >
                      Property Configuration
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <InputFieldset
                        label="Check-In Time"
                        required
                        id="checkInTime"
                        type="time"
                        value={propertyData.checkInTime}
                        leftIcon={<Clock className="w-4 h-4 text-gray-400" />}
                        onChange={(e) =>
                          setPropertyData({
                            ...propertyData,
                            checkInTime: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Check-Out Time"
                        required
                        id="checkOutTime"
                        type="time"
                        value={propertyData.checkOutTime}
                        leftIcon={<Clock className="w-4 h-4 text-gray-400" />}
                        onChange={(e) =>
                          setPropertyData({
                            ...propertyData,
                            checkOutTime: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Early Check-in Time"
                        id="earlyCheckInHours"
                        type="time"
                        value={propertyData.earlyCheckInHours}
                        leftIcon={<Clock className="w-4 h-4 text-gray-400" />}
                        onChange={(e) =>
                          setPropertyData({
                            ...propertyData,
                            earlyCheckInHours: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Late Check-out Time"
                        id="lateCheckInHours"
                        type="time"
                        value={propertyData.lateCheckInHours}
                        leftIcon={<Clock className="w-4 h-4 text-gray-400" />}
                        onChange={(e) =>
                          setPropertyData({
                            ...propertyData,
                            lateCheckInHours: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Extra Late Check-out Time"
                        id="lateCheckOutHours"
                        type="time"
                        value={propertyData.lateCheckOutHours}
                        leftIcon={<Clock className="w-4 h-4 text-gray-400" />}
                        onChange={(e) =>
                          setPropertyData({
                            ...propertyData,
                            lateCheckOutHours: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Property Amenities */}
                <div className="space-y-4">
                  <Accordion
                    type="single"
                    collapsible
                    className="w-full border rounded-lg"
                  >
                    <AccordionItem
                      value="property-amenities"
                      className="border-0"
                    >
                      <AccordionTrigger className="px-4 py-3 hover:no-underline bg-gradient-to-r from-sky-50 to-blue-50 rounded-t-lg w-full [&>svg]:ml-auto">
                        <h3
                          className="text-base font-semibold"
                          style={{ color: "#5B9BD5" }}
                        >
                          Property Amenities
                        </h3>
                      </AccordionTrigger>
                      <AccordionContent className="px-0 pb-0">
                        <div className="px-4 pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 bg-gray-50 border rounded-lg">
                            {propertyAmenities.map((amenity) => (
                              <div
                                key={amenity.id}
                                className="flex items-center justify-between gap-3 p-2 bg-white rounded border hover:border-[#5B9BD5]/30 transition-colors"
                              >
                                <label className="text-xs text-gray-700">
                                  {amenity.name}
                                </label>
                                <Checkbox
                                  checked={amenity.isSelected}
                                  onCheckedChange={(checked) => {
                                    setPropertyAmenities((prev) =>
                                      prev.map((a) =>
                                        a.id === amenity.id
                                          ? {
                                              ...a,
                                              isSelected: checked as boolean,
                                            }
                                          : a,
                                      ),
                                    );
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-3">
                            * Select amenities available at the property.
                          </p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {isHotelProperty && (
                  <div>
                    <Accordion
                      type="single"
                      collapsible
                      value={hotelContractAccordionValue}
                      onValueChange={setHotelContractAccordionValue}
                      className="w-full border rounded-xl bg-white shadow-sm"
                      data-section="hotel-contract"
                    >
                      <AccordionItem
                        value="hotel-contract-details"
                        className="border-0"
                      >
                        <AccordionTrigger className="px-5 py-4 hover:no-underline bg-sky-50 rounded-t-xl w-full [&>svg]:ml-auto">
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 w-full pr-4">
                            <div>
                              <h3
                                className="text-base font-semibold flex items-center gap-2"
                                style={{ color: "#5B9BD5" }}
                              >
                                <Building2 className="w-5 h-5" />
                                Hotel Contract Details
                              </h3>
                              <p className="text-sm text-gray-500 mt-1 text-left">
                                Manage agreement terms, rent cycle and room type
                                wise contract pricing.
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs uppercase tracking-wide text-gray-500">
                                Grand Total
                              </p>
                              <p className="text-2xl font-semibold text-gray-900">
                                {hotelContract.currency}{" "}
                                {hotelContractGrandTotal.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </AccordionTrigger>

                        <AccordionContent className="px-0 pb-0">
                          <div className="p-5 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <InputFieldset
                                label="Contract Reference No."
                                id="hotel-contract-reference"
                                value={hotelContract.contractReferenceNo}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /[^a-zA-Z0-9_/-]/g,
                                    "",
                                  );

                                  setHotelContract({
                                    ...hotelContract,
                                    contractReferenceNo: value,
                                  });
                                }}
                                placeholder="e.g., HC-2026-001"
                              />

                              <InputFieldset
                                label="Contract Start Date"
                                id="hotel-contract-start"
                                type="date"
                                min={new Date().toISOString().split("T")[0]}
                                value={hotelContract.contractStartDate}
                                onChange={(e) => {
                                  const startDate = e.target.value;

                                  if (
                                    hotelContract.contractEndDate &&
                                    hotelContract.contractEndDate < startDate
                                  ) {
                                    toast.info(
                                      "Contract End Date has been cleared because it cannot be earlier than the Contract Start Date.",
                                    );
                                  }

                                  setHotelContract((prev) => ({
                                    ...prev,
                                    contractStartDate: startDate,
                                    contractEndDate:
                                      prev.contractEndDate &&
                                      prev.contractEndDate < startDate
                                        ? ""
                                        : prev.contractEndDate,
                                  }));
                                }}
                              />

                              <InputFieldset
                                label="Contract End Date"
                                id="hotel-contract-end"
                                type="date"
                                min={
                                  hotelContract.contractStartDate ||
                                  new Date().toISOString().split("T")[0]
                                }
                                value={hotelContract.contractEndDate}
                                onChange={(e) =>
                                  setHotelContract({
                                    ...hotelContract,
                                    contractEndDate: e.target.value,
                                  })
                                }
                              />
                              <SelectFieldset label="Currency">
                                <Select
                                  value={hotelContract.currency}
                                  onValueChange={(value) =>
                                    setHotelContract({
                                      ...hotelContract,
                                      currency: value,
                                    })
                                  }
                                >
                                  <SelectTriggerUI>
                                    <SelectValue placeholder="Currency" />
                                  </SelectTriggerUI>
                                  <SelectContent>
                                    {currencyOptions.map((currency) => (
                                      <SelectItem
                                        key={currency.value}
                                        value={currency.value}
                                      >
                                        {currency.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </SelectFieldset>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <SelectFieldset label="Billing Cycle">
                                <Select
                                  value={hotelContract.billingCycle}
                                  onValueChange={(value) =>
                                    setHotelContract({
                                      ...hotelContract,
                                      billingCycle: value,
                                    })
                                  }
                                >
                                  <SelectTriggerUI>
                                    <SelectValue placeholder="Billing cycle" />
                                  </SelectTriggerUI>
                                  <SelectContent>
                                    <SelectItem value="daily">Daily</SelectItem>
                                    <SelectItem value="weekly">
                                      Weekly
                                    </SelectItem>
                                    <SelectItem value="monthly">
                                      Monthly
                                    </SelectItem>
                                    <SelectItem value="seasonal">
                                      Seasonal
                                    </SelectItem>
                                    <SelectItem value="contract-period">
                                      Full Contract Period
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </SelectFieldset>

                              <SelectFieldset label="Payment Terms">
                                <Select
                                  value={hotelContract.paymentTerms}
                                  onValueChange={(value) =>
                                    setHotelContract({
                                      ...hotelContract,
                                      paymentTerms: value,
                                    })
                                  }
                                >
                                  <SelectTriggerUI>
                                    <SelectValue placeholder="Payment terms" />
                                  </SelectTriggerUI>
                                  <SelectContent>
                                    <SelectItem value="prepaid">
                                      Prepaid
                                    </SelectItem>
                                    <SelectItem value="postpaid">
                                      Postpaid
                                    </SelectItem>
                                    <SelectItem value="advance-50">
                                      50% Advance
                                    </SelectItem>
                                    <SelectItem value="milestone">
                                      Milestone Based
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </SelectFieldset>

                              <InputFieldset
                                label="Notice Period (Days)"
                                id="hotel-notice-period"
                                type="number"
                                min="0"
                                value={hotelContract.noticePeriodDays || ""}
                                onChange={(e) =>
                                  setHotelContract({
                                    ...hotelContract,
                                    noticePeriodDays: Number(
                                      e.target.value || 0,
                                    ),
                                  })
                                }
                                placeholder="30"
                              />

                              <InputFieldset
                                label="Security Deposit"
                                id="hotel-security-deposit"
                                type="number"
                                min="0"
                                value={hotelContract.securityDeposit || ""}
                                onChange={(e) =>
                                  setHotelContract({
                                    ...hotelContract,
                                    securityDeposit: Number(
                                      e.target.value || 0,
                                    ),
                                  })
                                }
                                placeholder="0.00"
                              />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <InputFieldset
                                label="Tax / Service Charge (%)"
                                id="hotel-tax-percentage"
                                type="number"
                                min="0"
                                value={hotelContract.taxPercentage || ""}
                                onChange={(e) =>
                                  setHotelContract({
                                    ...hotelContract,
                                    taxPercentage: Number(e.target.value || 0),
                                  })
                                }
                                placeholder="0"
                              />

                              <InputFieldset
                                label="Included Services"
                                id="hotel-included-services"
                                value={hotelContract.includedServices}
                                onChange={(e) =>
                                  setHotelContract({
                                    ...hotelContract,
                                    includedServices: e.target.value,
                                  })
                                }
                                placeholder="Housekeeping, utilities, breakfast, maintenance"
                              />
                              <InputFieldset
                                label="Cancellation Policy"
                                id="hotel-cancellation-policy"
                                value={hotelContract.cancellationPolicy}
                                onChange={(e) =>
                                  setHotelContract({
                                    ...hotelContract,
                                    cancellationPolicy: e.target.value,
                                  })
                                }
                                placeholder="e.g., 30 days notice before start date"
                              />
                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-[#1E3A5F]">
                                  Agreement Document
                                </Label>

                                <label className="w-full h-10 px-3 rounded-md border border-input bg-white hover:bg-sky-50 cursor-pointer flex items-center gap-2 text-sm">
                                  <Upload className="w-4 h-4 text-[#5B9BD5] flex-shrink-0" />

                                  {hotelContract.agreementDocumentUrl ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewAgreementDocument();
                                      }}
                                      className="truncate text-[#5B9BD5] underline text-left"
                                    >
                                      {hotelContract.agreementDocumentName ||
                                        (hotelContract.agreementDocumentUrl
                                          ? "Agreement Document Uploaded"
                                          : "No file selected")}
                                    </button>
                                  ) : (
                                    <span className="truncate text-gray-700">
                                      Upload agreement...
                                    </span>
                                  )}

                                  <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                                    onChange={(e) =>
                                      handleAgreementDocumentUpload(
                                        e.target.files?.[0],
                                      )
                                    }
                                  />
                                </label>
                              </div>
                            </div>

                            {/* Room Type Configuration */}
                            <div
                              className="space-y-4"
                              data-section="room-config"
                            >
                              <div className="border-b pb-4 space-y-3">
                                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                  <div className="min-w-0">
                                    <h3
                                      className="text-base font-semibold flex items-center gap-2 whitespace-nowrap"
                                      style={{ color: "#5B9BD5" }}
                                    >
                                      <DoorOpen className="w-5 h-5" />
                                      Room Type Configuration
                                      {propertyData.propertyType !== "Faiz" &&
                                        propertyData.propertyType !== "" && (
                                          <span className="text-red-500">
                                            *
                                          </span>
                                        )}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                      {roomTypeConfigs.length > 0
                                        ? "Review added room configurations below."
                                        : propertyData.propertyType !==
                                              "Faiz" &&
                                            propertyData.propertyType !== ""
                                          ? "Add at least one room type configuration (Required)"
                                          : "Add different room types for this property"}
                                    </p>
                                  </div>
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      setIsEditingRoomTypeConfig(false);
                                      setCurrentRoomTypeConfig({
                                        id: "",
                                        roomTypeIds: [""],
                                        pricePerRoom: 0,
                                        maxOccupancy: 2,
                                        maxAdults: 2,
                                        maxChildren: 0,
                                        minAdults: 1,
                                        minOccupancy: 1,
                                        childAgeMax: 12,
                                        numberOfRooms: 1,
                                        roomNames: [],
                                        personTypes: [],
                                        amenities: roomAmenities.map((a) => ({
                                          ...a,
                                          isSelected: false,
                                        })),
                                      });
                                      setIsRoomTypeDialogOpen(true);
                                    }}
                                    style={{ background: "#5B9BD5" }}
                                    className="text-white lg:w-36"
                                  >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Room
                                  </Button>
                                </div>
                              </div>

                              {/* Add Room Type Form */}
                              <Dialog
                                open={isRoomTypeDialogOpen}
                                onOpenChange={(open) => {
                                  setIsRoomTypeDialogOpen(open);
                                  if (!open) {
                                    setIsEditingRoomTypeConfig(false);
                                  }
                                }}
                              >
                                <DialogContent className="max-w-[96vw] lg:max-w-[1100px] max-h-[90vh] overflow-y-auto">
                                  <DialogHeader>
                                    <DialogTitle style={{ color: "#5B9BD5" }}>
                                      {isEditingRoomTypeConfig
                                        ? "Edit Room"
                                        : "Add Room"}
                                    </DialogTitle>
                                  </DialogHeader>
                                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                                    <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-blue-800 leading-snug break-words">
                                      <strong>
                                        Pricing Configuration Moved:
                                      </strong>{" "}
                                      Room pricing is now managed centrally.
                                      Please use the{" "}
                                      <strong>
                                        Room Type Pricing Configuration
                                      </strong>{" "}
                                      or <strong>Lawazim Configuration</strong>{" "}
                                      modules to set prices for these room
                                      types.
                                    </p>
                                  </div>
                                  <Card className="border-[#5B9BD5]/30 bg-white">
                                    <CardContent className="p-4 space-y-4">
                                      {/* Floor Type, Room Type */}
                                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        {/* <div className="space-y-2">
                          <SelectFieldset label="Floor Type" required>
                            {isEditingRoomTypeConfig ? (
                              <Select
                                value={currentRoomTypeConfig.floorTypes[0] || ""}
                                onValueChange={(value) =>
                                  setCurrentRoomTypeConfig({
                                    ...currentRoomTypeConfig,
                                    floorTypes: [value],
                                  })
                                }
                              >
                                <SelectTriggerUI>
                                  <SelectValue placeholder="Select floor type" />
                                </SelectTriggerUI>
                                <SelectContent>
                                  {floorTypeOptions.map((option) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                          ) : (
                            <MultiSelect
                              options={floorTypeOptions}
                              selected={currentRoomTypeConfig.floorTypes}
                              onChange={(selected) =>
                                setCurrentRoomTypeConfig({
                                  ...currentRoomTypeConfig,
                                  floorTypes: selected,
                                })
                              }
                              closeOnSelect // ✅ ADD THIS (if supported)
                              placeholder="Select floor types..."
                            />
                          )}
                          </SelectFieldset>
                        </div> */}

                                        <div className="space-y-2">
                                          <SelectFieldset
                                            label="Room Type"
                                            required
                                          >
                                            <Select
                                              value={
                                                currentRoomTypeConfig
                                                  .roomTypeIds[0] || ""
                                              }
                                              onValueChange={(value) =>
                                                setCurrentRoomTypeConfig({
                                                  ...currentRoomTypeConfig,
                                                  roomTypeIds: [value],
                                                })
                                              }
                                            >
                                              <SelectTriggerUI>
                                                <SelectValue placeholder="Select room type..." />
                                              </SelectTriggerUI>
                                              <SelectContent>
                                                {getAvailableRoomTypeOptions().map(
                                                  (option) => (
                                                    <SelectItem
                                                      key={option.value}
                                                      value={option.value.toString()}
                                                    >
                                                      {option.label}
                                                    </SelectItem>
                                                  ),
                                                )}
                                              </SelectContent>
                                            </Select>
                                          </SelectFieldset>
                                        </div>

                                        <div className="space-y-2">
                                          <InputFieldset
                                            label="Number of Rooms"
                                            required
                                            id="config-numberOfRooms"
                                            type="number"
                                            min="1"
                                            max="200"
                                            value={
                                              currentRoomTypeConfig.numberOfRooms ||
                                              ""
                                            }
                                            onChange={(e) => {
                                              let roomCount = parseInt(
                                                e.target.value,
                                              );

                                              if (isNaN(roomCount))
                                                roomCount = 1;
                                              if (roomCount < 1) roomCount = 1;
                                              if (roomCount > 200)
                                                roomCount = 200;

                                              setCurrentRoomTypeConfig(
                                                (prev) => {
                                                  const updatedRoomNames = [
                                                    ...(prev.roomNames || []),
                                                  ];

                                                  // expand
                                                  while (
                                                    updatedRoomNames.length <
                                                    roomCount
                                                  ) {
                                                    updatedRoomNames.push("");
                                                  }

                                                  // shrink
                                                  if (
                                                    updatedRoomNames.length >
                                                    roomCount
                                                  ) {
                                                    updatedRoomNames.length =
                                                      roomCount;
                                                  }

                                                  return {
                                                    ...prev,
                                                    numberOfRooms: roomCount,
                                                    roomNames: updatedRoomNames,
                                                  };
                                                },
                                              );
                                            }}
                                            placeholder="1"
                                          />
                                        </div>

                                        <div className="space-y-2">
                                          <InputFieldset
                                            label="Child Age Max (Years)"
                                            id="config-childAgeMax"
                                            type="number"
                                            min="0"
                                            max="200"
                                            value={
                                              currentRoomTypeConfig.childAgeMax ||
                                              ""
                                            }
                                            onChange={(e) => {
                                              let childAgeMax = parseInt(
                                                e.target.value,
                                              );

                                              if (isNaN(childAgeMax))
                                                childAgeMax = 1;
                                              if (childAgeMax < 1)
                                                childAgeMax = 1;
                                              if (childAgeMax > 200)
                                                childAgeMax = 200;

                                              setCurrentRoomTypeConfig({
                                                ...currentRoomTypeConfig,
                                                childAgeMax,
                                              });
                                            }}
                                            placeholder="12"
                                          />
                                        </div>

                                        {isHotelProperty && (
                                          <div className="space-y-2">
                                            <InputFieldset
                                              label={`Rent Per Room (${hotelContract.currency})`}
                                              id="config-pricePerRoom"
                                              type="number"
                                              min="0"
                                              value={
                                                currentRoomTypeConfig.pricePerRoom ||
                                                ""
                                              }
                                              onChange={(e) =>
                                                setCurrentRoomTypeConfig({
                                                  ...currentRoomTypeConfig,
                                                  pricePerRoom: Number(
                                                    e.target.value || 0,
                                                  ),
                                                })
                                              }
                                              placeholder="0.00"
                                            />
                                          </div>
                                        )}
                                      </div>

                                      {/* Room Names Input (appears when numberOfRooms > 0) */}
                                      {currentRoomTypeConfig.numberOfRooms >
                                        0 && (
                                        <div className="space-y-2">
                                          <Label className="text-sm font-medium">
                                            Room Names{" "}
                                            <span className="text-xs text-gray-500 font-normal">
                                              (Optional - manually name each
                                              room)
                                            </span>
                                          </Label>

                                          <div
                                            className={`grid grid-cols-2 md:grid-cols-5 gap-3 p-3 border rounded-lg ${
                                              currentRoomTypeConfig.numberOfRooms >
                                              20
                                                ? "h-80 overflow-y-auto"
                                                : ""
                                            }`}
                                          >
                                            {Array.from({
                                              length:
                                                currentRoomTypeConfig.numberOfRooms,
                                            }).map((_, index) => (
                                              <div
                                                key={index}
                                                className="space-y-1"
                                              >
                                                <InputFieldset
                                                  label={`Room ${index + 1}`}
                                                  required
                                                  id={`room-name-${index}`}
                                                  type="text"
                                                  onKeyDown={(e) => {
                                                    const columns =
                                                      window.innerWidth >= 768
                                                        ? 5
                                                        : 2;

                                                    let nextIndex = index;

                                                    switch (e.key) {
                                                      case "ArrowRight":
                                                        nextIndex = index + 1;
                                                        break;

                                                      case "ArrowLeft":
                                                        nextIndex = index - 1;
                                                        break;

                                                      case "ArrowDown":
                                                        nextIndex =
                                                          index + columns;
                                                        break;

                                                      case "ArrowUp":
                                                        nextIndex =
                                                          index - columns;
                                                        break;

                                                      default:
                                                        return;
                                                    }

                                                    if (
                                                      nextIndex >= 0 &&
                                                      nextIndex <
                                                        currentRoomTypeConfig.numberOfRooms
                                                    ) {
                                                      e.preventDefault();

                                                      const nextInput =
                                                        document.getElementById(
                                                          `room-name-${nextIndex}`,
                                                        ) as HTMLInputElement | null;

                                                      nextInput?.focus();
                                                    }
                                                  }}
                                                  value={
                                                    currentRoomTypeConfig
                                                      .roomNames?.[index] || ""
                                                  }
                                                  onChange={(e) => {
                                                    const value =
                                                      e.target.value;

                                                    setCurrentRoomTypeConfig(
                                                      (prev) => {
                                                        const updatedRoomNames =
                                                          [
                                                            ...(prev.roomNames ||
                                                              []),
                                                          ];

                                                        while (
                                                          updatedRoomNames.length <
                                                          prev.numberOfRooms
                                                        ) {
                                                          updatedRoomNames.push(
                                                            "",
                                                          );
                                                        }

                                                        updatedRoomNames[
                                                          index
                                                        ] = value;

                                                        return {
                                                          ...prev,
                                                          roomNames:
                                                            updatedRoomNames,
                                                        };
                                                      },
                                                    );
                                                  }}
                                                  placeholder={`e.g., ${index + 101}`}
                                                  className="text-sm"
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Occupancy */}
                                      <div className="grid grid-cols-1 md:grid-cols-[0.75fr_0.85fr_0.95fr_1.35fr_1.4fr] gap-3">
                                        <div className="space-y-2">
                                          <InputFieldset
                                            label="Min. Adults"
                                            required
                                            id="config-minAdults"
                                            type="number"
                                            min="1"
                                            max="200"
                                            value={
                                              currentRoomTypeConfig.minAdults ||
                                              ""
                                            }
                                            onChange={(e) => {
                                              let minAdults = parseInt(
                                                e.target.value,
                                              );

                                              if (isNaN(minAdults))
                                                minAdults = 1;
                                              if (minAdults < 1) minAdults = 1;
                                              if (minAdults > 200)
                                                minAdults = 200;

                                              setCurrentRoomTypeConfig({
                                                ...currentRoomTypeConfig,
                                                minAdults,
                                              });
                                            }}
                                            placeholder="1"
                                          />
                                        </div>

                                        <div className="space-y-2">
                                          <InputFieldset
                                            label="Max Adults"
                                            required
                                            id="config-maxAdults"
                                            type="number"
                                            min="1"
                                            max="200"
                                            value={
                                              currentRoomTypeConfig.maxAdults ||
                                              ""
                                            }
                                            onChange={(e) => {
                                              let adults = parseInt(
                                                e.target.value,
                                              );

                                              if (isNaN(adults)) adults = 1;
                                              if (adults < 1) adults = 1;
                                              if (adults > 200) adults = 200;

                                              setCurrentRoomTypeConfig({
                                                ...currentRoomTypeConfig,
                                                maxAdults: adults,
                                                maxOccupancy: Math.min(
                                                  200,
                                                  adults +
                                                    currentRoomTypeConfig.maxChildren,
                                                ),
                                              });
                                            }}
                                            placeholder="2"
                                          />
                                        </div>

                                        <div className="space-y-2">
                                          <InputFieldset
                                            label="Max Children"
                                            required
                                            id="config-maxChildren"
                                            type="number"
                                            min="1"
                                            max="200"
                                            value={
                                              currentRoomTypeConfig.maxChildren ||
                                              ""
                                            }
                                            onChange={(e) => {
                                              let children = parseInt(
                                                e.target.value,
                                              );

                                              if (isNaN(children)) children = 1;
                                              if (children < 1) children = 1;
                                              if (children > 200)
                                                children = 200;

                                              setCurrentRoomTypeConfig({
                                                ...currentRoomTypeConfig,
                                                maxChildren: children,
                                                maxOccupancy: Math.min(
                                                  200,
                                                  currentRoomTypeConfig.maxAdults +
                                                    children,
                                                ),
                                              });
                                            }}
                                            placeholder="2"
                                          />
                                        </div>

                                        <div className="space-y-2">
                                          <InputFieldset
                                            label="Min. Occupancy (Total)"
                                            required
                                            id="config-minOccupancy"
                                            type="number"
                                            min="1"
                                            max="200"
                                            value={
                                              currentRoomTypeConfig.minOccupancy ||
                                              ""
                                            }
                                            onChange={(e) => {
                                              let minOccupancy = parseInt(
                                                e.target.value,
                                              );

                                              if (isNaN(minOccupancy))
                                                minOccupancy = 1;
                                              if (minOccupancy < 1)
                                                minOccupancy = 1;
                                              if (minOccupancy > 200)
                                                minOccupancy = 200;

                                              setCurrentRoomTypeConfig({
                                                ...currentRoomTypeConfig,
                                                minOccupancy,
                                              });
                                            }}
                                            placeholder="1"
                                          />
                                        </div>

                                        <div className="space-y-2">
                                          {/* <InputFieldset
                                            label="Max Occupancy (Total)"
                                            id="config-maxOccupancy"
                                            type="number"
                                            value={
                                              currentRoomTypeConfig.maxOccupancy
                                            }
                                            disabled
                                            className="bg-gray-100"
                                          /> */}
                                          <InputFieldset
                                            label="Max Occupancy (Total)"
                                            id="config-maxOccupancy"
                                            type="number"
                                            min="1"
                                            max="200"
                                            value={
                                              currentRoomTypeConfig.maxOccupancy
                                            }
                                            onChange={(e) => {
                                              let occupancy = parseInt(
                                                e.target.value,
                                              );

                                              if (isNaN(occupancy))
                                                occupancy = 1;
                                              if (occupancy < 1) occupancy = 1;
                                              if (occupancy > 200)
                                                occupancy = 200;

                                              setCurrentRoomTypeConfig({
                                                ...currentRoomTypeConfig,
                                                maxOccupancy: occupancy,
                                              });
                                            }}
                                          />
                                        </div>

                                        {/* <div className="space-y-2">
                          <InputFieldset
                            label="Max Extra Beds Allowed"
                            id="config-maxExtraBeds"
                            type="number"
                            min="0"
                            value={currentRoomTypeConfig.maxExtraBeds || ""}
                            onChange={(e) =>
                              setCurrentRoomTypeConfig({
                                ...currentRoomTypeConfig,
                                maxExtraBeds: parseInt(e.target.value) || 0,
                              })
                            }
                            placeholder="0"
                          />
                        </div> */}
                                      </div>

                                      {/* Room Amenities */}
                                      <Accordion
                                        type="single"
                                        collapsible
                                        className="w-full border rounded-lg"
                                      >
                                        <AccordionItem
                                          value="room-amenities"
                                          className="border-0"
                                        >
                                          <AccordionTrigger className="px-4 py-3 hover:no-underline bg-gradient-to-r from-sky-50 to-blue-50 rounded-t-lg w-full [&>svg]:ml-auto">
                                            <h4
                                              className="text-sm font-semibold"
                                              style={{ color: "#5B9BD5" }}
                                            >
                                              Room Amenities
                                            </h4>
                                          </AccordionTrigger>
                                          <AccordionContent className="px-0 pb-0">
                                            <div className="px-4 pt-3">
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 bg-gray-50 border rounded-lg">
                                                {(
                                                  currentRoomTypeConfig.amenities ||
                                                  []
                                                ).map((amenity) => (
                                                  <div
                                                    key={amenity.id}
                                                    className="flex items-center justify-between gap-3 p-2 bg-white rounded border hover:border-[#5B9BD5]/30 transition-colors"
                                                  >
                                                    <label className="text-xs text-gray-700">
                                                      {amenity.name}
                                                    </label>
                                                    <Checkbox
                                                      checked={
                                                        amenity.isSelected
                                                      }
                                                      onCheckedChange={(
                                                        checked,
                                                      ) => {
                                                        setCurrentRoomTypeConfig(
                                                          (prev) => ({
                                                            ...prev,
                                                            amenities: (
                                                              prev.amenities ||
                                                              []
                                                            ).map((a) =>
                                                              a.id ===
                                                              amenity.id
                                                                ? {
                                                                    ...a,
                                                                    isSelected:
                                                                      checked as boolean,
                                                                  }
                                                                : a,
                                                            ),
                                                          }),
                                                        );
                                                      }}
                                                    />
                                                  </div>
                                                ))}
                                              </div>
                                              <p className="text-xs text-gray-500 mt-3">
                                                * Select amenities available in
                                                this room type.
                                              </p>
                                            </div>
                                          </AccordionContent>
                                        </AccordionItem>
                                      </Accordion>

                                      {/* Add/Update Button */}
                                      {isEditingRoomTypeConfig ? (
                                        <div className="flex gap-2">
                                          <Button
                                            type="button"
                                            onClick={() => {
                                              setIsEditingRoomTypeConfig(false);
                                              setCurrentRoomTypeConfig({
                                                id: "",
                                                roomTypeIds: [""],
                                                pricePerRoom: 0,
                                                maxOccupancy: 2,
                                                maxAdults: 2,
                                                maxChildren: 0,
                                                minAdults: 1,
                                                minOccupancy: 1,
                                                childAgeMax: 12,
                                                numberOfRooms: 1,
                                                roomNames: [],
                                                personTypes: [],
                                                amenities: [],
                                              });
                                              setIsRoomTypeDialogOpen(false);
                                              toast.info("Edit cancelled");
                                            }}
                                            variant="outline"
                                            className="flex-1"
                                          >
                                            <X className="w-4 h-4 mr-2" />
                                            Cancel
                                          </Button>
                                          <Button
                                            type="button"
                                            onClick={handleAddRoomTypeConfig}
                                            className="flex-1"
                                            style={{ background: "#5B9BD5" }}
                                          >
                                            <Edit2 className="w-4 h-4 mr-2" />
                                            Update Room
                                          </Button>
                                        </div>
                                      ) : (
                                        <Button
                                          type="button"
                                          onClick={handleAddRoomTypeConfig}
                                          className="w-full"
                                          style={{ background: "#5B9BD5" }}
                                        >
                                          <Plus className="w-4 h-4 mr-2" />
                                          Add Room
                                        </Button>
                                      )}
                                    </CardContent>
                                  </Card>
                                </DialogContent>
                              </Dialog>

                              {isHotelProperty && (
                                <div
                                  className="space-y-4"
                                  data-section="hotel-room-rent"
                                >
                                  <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
                                    <div className="p-5 space-y-5">
                                      <div className="border rounded-lg overflow-hidden">
                                        <div className="px-4 py-3 bg-amber-50 border-b flex flex-col md:flex-row md:items-center justify-between gap-2">
                                          <div>
                                            <h4 className="font-semibold text-gray-900">
                                              Room Configurations
                                            </h4>
                                            <p className="text-xs text-gray-500">
                                              Room rent is entered in the
                                              Add/Edit Room popup for hotel
                                              properties.
                                            </p>
                                          </div>
                                          <div className="text-sm font-semibold text-gray-900">
                                            Room Rent Total:{" "}
                                            {hotelContract.currency}{" "}
                                            {hotelRoomRentTotal.toFixed(2)}
                                          </div>
                                        </div>

                                        {roomTypeConfigs.length > 0 ? (
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                              <thead className="bg-gray-50 border-b">
                                                <tr className="text-left text-gray-700">
                                                  <th className="px-4 py-3 font-semibold">
                                                    Room Type
                                                  </th>
                                                  <th className="px-4 py-3 font-semibold text-center">
                                                    Rooms
                                                  </th>
                                                  <th className="px-4 py-3 font-semibold">
                                                    Capacity
                                                  </th>
                                                  <th className="px-4 py-3 font-semibold">
                                                    Child Age
                                                  </th>
                                                  <th className="px-4 py-3 font-semibold">
                                                    Room Names
                                                  </th>
                                                  <th className="px-4 py-3 font-semibold">
                                                    Amenities
                                                  </th>
                                                  <th className="px-4 py-3 font-semibold">
                                                    Rent Per Room
                                                  </th>
                                                  <th className="px-4 py-3 font-semibold text-right">
                                                    Total Rent
                                                  </th>
                                                  <th className="px-4 py-3 font-semibold text-center">
                                                    Action
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {roomTypeConfigs.map(
                                                  (config) => {
                                                    const rentPerRoom = Number(
                                                      config.pricePerRoom || 0,
                                                    );
                                                    const rowTotal =
                                                      rentPerRoom *
                                                      Number(
                                                        config.numberOfRooms ||
                                                          0,
                                                      );
                                                    const roomNames = (
                                                      config.roomNames || []
                                                    ).filter(Boolean);
                                                    const propertyAmenitiesList =
                                                      getPropertyAmenities();
                                                    const roomAmenitiesList =
                                                      getRoomTypeAmenities(
                                                        config,
                                                      );
                                                    const amenitiesList = [
                                                      ...propertyAmenitiesList,
                                                      ...roomAmenitiesList,
                                                    ];

                                                    return (
                                                      <tr
                                                        key={config.id}
                                                        className="border-b last:border-b-0"
                                                      >
                                                        <td className="px-4 py-3 font-medium text-gray-900">
                                                          {getRoomTypeName(
                                                            config
                                                              .roomTypeIds[0],
                                                          )}
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-gray-700">
                                                          {config.numberOfRooms}
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-700 min-w-[120px]">
                                                          <div className="font-medium text-gray-900">
                                                            Min:{" "}
                                                            {config.minOccupancy ||
                                                              config.minAdults ||
                                                              1}{" "}
                                                            / Max:{" "}
                                                            {
                                                              config.maxOccupancy
                                                            }
                                                          </div>
                                                          <div className="text-xs text-gray-500">
                                                            {config.maxAdults}A
                                                            {config.maxChildren >
                                                            0
                                                              ? " + " +
                                                                config.maxChildren +
                                                                "C"
                                                              : ""}
                                                          </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                                          {config.childAgeMax ||
                                                            propertyData.childAgeMax ||
                                                            0}{" "}
                                                          Yrs
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-700 min-w-[160px]">
                                                          {roomNames.length > 0
                                                            ? roomNames.join(
                                                                ", ",
                                                              )
                                                            : "-"}
                                                        </td>
                                                        <td className="px-4 py-3 min-w-[180px]">
                                                          <div className="flex flex-wrap gap-1">
                                                            {amenitiesList.length >
                                                            0 ? (
                                                              amenitiesList.map(
                                                                (
                                                                  amenity,
                                                                  index,
                                                                ) => (
                                                                  <Badge
                                                                    key={
                                                                      config.id +
                                                                      "-amenity-" +
                                                                      index
                                                                    }
                                                                    variant="outline"
                                                                    className="bg-[#5B9BD5]/10 text-[#2563eb] border-[#5B9BD5]/30"
                                                                  >
                                                                    {amenity}
                                                                  </Badge>
                                                                ),
                                                              )
                                                            ) : (
                                                              <span className="text-gray-500">
                                                                -
                                                              </span>
                                                            )}
                                                          </div>
                                                        </td>
                                                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                                          {
                                                            hotelContract.currency
                                                          }{" "}
                                                          {rentPerRoom.toFixed(
                                                            2,
                                                          )}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                                                          {
                                                            hotelContract.currency
                                                          }{" "}
                                                          {rowTotal.toFixed(2)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                          <div className="flex justify-center gap-1">
                                                            {isEditMode && (
                                                              <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() =>
                                                                  handleEditRoomTypeConfig(
                                                                    config,
                                                                  )
                                                                }
                                                                className="text-[#5B9BD5] hover:bg-[#5B9BD5]/10 h-8 w-8 p-0"
                                                              >
                                                                <Edit2 className="w-4 h-4" />
                                                              </Button>
                                                            )}
                                                            <Button
                                                              type="button"
                                                              size="sm"
                                                              variant="ghost"
                                                              onClick={() =>
                                                                handleRemoveRoomTypeConfig(
                                                                  config.id,
                                                                )
                                                              }
                                                              className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                                                            >
                                                              <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                          </div>
                                                        </td>
                                                      </tr>
                                                    );
                                                  },
                                                )}
                                              </tbody>
                                            </table>
                                          </div>
                                        ) : (
                                          <div className="p-4 text-sm text-amber-800 bg-amber-50 flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                            Please add at least one room type
                                            configuration below before saving
                                            the property.
                                          </div>
                                        )}
                                      </div>

                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="p-4 rounded-lg bg-gray-50 border">
                                          <p className="text-xs uppercase tracking-wide text-gray-500">
                                            Room Rent Total
                                          </p>
                                          <p className="text-xl font-semibold text-gray-900">
                                            {hotelContract.currency}{" "}
                                            {hotelRoomRentTotal.toFixed(2)}
                                          </p>
                                        </div>
                                        <div className="p-4 rounded-lg bg-gray-50 border">
                                          <p className="text-xs uppercase tracking-wide text-gray-500">
                                            Tax / Service Charge
                                          </p>
                                          <p className="text-xl font-semibold text-gray-900">
                                            {hotelContract.currency}{" "}
                                            {hotelTaxAmount.toFixed(2)}
                                          </p>
                                        </div>
                                        <div className="p-4 rounded-lg bg-sky-50 border border-sky-200">
                                          <p className="text-xs uppercase tracking-wide text-sky-700">
                                            Contract Grand Total
                                          </p>
                                          <p className="text-xl font-semibold text-gray-900">
                                            {hotelContract.currency}{" "}
                                            {hotelContractGrandTotal.toFixed(2)}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="flex flex-col sm:flex-row justify-end gap-2">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          onClick={() => {
                                            resetHotelContractForm();
                                            setEditingHotelContractId(null);
                                            setRoomTypeConfigs([]);
                                          }}
                                          className="sm:w-36"
                                        >
                                          Clear Contract
                                        </Button>
                                        <Button
                                          type="button"
                                          onClick={handleAddHotelContract}
                                          className="sm:w-40"
                                          style={{ background: "#5B9BD5" }}
                                        >
                                          <Plus className="w-4 h-4 mr-2" />
                                          {hotelContract.hotelContractId
                                            ? "Update Contract"
                                            : "Add Contract"}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {roomTypeConfigs.length === 0 &&
                                !isHotelProperty &&
                                propertyData.propertyType !== "Faiz" &&
                                propertyData.propertyType !== "" && (
                                  <div className="border rounded-lg overflow-hidden bg-white">
                                    <div className="p-4 text-sm text-amber-800 bg-amber-50 flex items-center gap-2">
                                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                      Please add at least one room type
                                      configuration below before saving the
                                      property.
                                    </div>
                                  </div>
                                )}

                              {/* Room type configurations will be listed here */}
                              {roomTypeConfigs.length > 0 &&
                                !isHotelProperty && (
                                  <div className="border rounded-lg overflow-hidden bg-white">
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead className="bg-gray-50 border-b">
                                          <tr className="text-left text-gray-700">
                                            <th className="px-4 py-3 font-semibold">
                                              Room Type
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-center">
                                              Rooms
                                            </th>
                                            <th className="px-4 py-3 font-semibold">
                                              Capacity
                                            </th>
                                            <th className="px-4 py-3 font-semibold">
                                              Child Age
                                            </th>
                                            <th className="px-4 py-3 font-semibold">
                                              Room Names
                                            </th>
                                            <th className="px-4 py-3 font-semibold">
                                              Amenities
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-center">
                                              Action
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {roomTypeConfigs.map((config) => {
                                            const roomType = getRoomTypeName(
                                              config.roomTypeIds[0],
                                            );
                                            const propertyAmenitiesList =
                                              getPropertyAmenities();
                                            const roomAmenitiesList =
                                              getRoomTypeAmenities(config);
                                            const amenitiesList = [
                                              ...propertyAmenitiesList,
                                              ...roomAmenitiesList,
                                            ];
                                            const roomNames = (
                                              config.roomNames || []
                                            ).filter(Boolean);

                                            return (
                                              <tr
                                                key={config.id}
                                                className="border-b last:border-b-0 align-top"
                                              >
                                                <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                                  {roomType}
                                                </td>
                                                <td className="px-4 py-3 text-center text-gray-700">
                                                  {config.numberOfRooms}
                                                </td>
                                                <td className="px-4 py-3 text-gray-700 min-w-[120px]">
                                                  <div className="font-medium text-gray-900">
                                                    Min:{" "}
                                                    {config.minOccupancy ||
                                                      config.minAdults ||
                                                      1}{" "}
                                                    / Max: {config.maxOccupancy}
                                                  </div>
                                                  <div className="text-xs text-gray-500">
                                                    {config.maxAdults}A
                                                    {config.maxChildren > 0
                                                      ? " + " +
                                                        config.maxChildren +
                                                        "C"
                                                      : ""}
                                                  </div>
                                                </td>
                                                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                                  {config.childAgeMax ||
                                                    propertyData.childAgeMax ||
                                                    0}{" "}
                                                  Yrs
                                                </td>
                                                <td className="px-4 py-3 text-gray-700 min-w-[160px]">
                                                  {roomNames.length > 0
                                                    ? roomNames.join(", ")
                                                    : "-"}
                                                </td>
                                                <td className="px-4 py-3 min-w-[220px]">
                                                  <div className="flex flex-wrap gap-1">
                                                    {amenitiesList.length >
                                                    0 ? (
                                                      amenitiesList.map(
                                                        (amenity, index) => (
                                                          <Badge
                                                            key={
                                                              config.id +
                                                              "-amenity-" +
                                                              index
                                                            }
                                                            variant="outline"
                                                            className="bg-[#5B9BD5]/10 text-[#2563eb] border-[#5B9BD5]/30"
                                                          >
                                                            {amenity}
                                                          </Badge>
                                                        ),
                                                      )
                                                    ) : (
                                                      <span className="text-gray-500">
                                                        -
                                                      </span>
                                                    )}
                                                  </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                  <div className="flex justify-center gap-1">
                                                    {isEditMode && (
                                                      <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() =>
                                                          handleEditRoomTypeConfig(
                                                            config,
                                                          )
                                                        }
                                                        className="text-[#5B9BD5] hover:bg-[#5B9BD5]/10 h-8 w-8 p-0"
                                                      >
                                                        <Edit2 className="w-4 h-4" />
                                                      </Button>
                                                    )}
                                                    <Button
                                                      type="button"
                                                      size="sm"
                                                      variant="ghost"
                                                      onClick={() =>
                                                        handleRemoveRoomTypeConfig(
                                                          config.id,
                                                        )
                                                      }
                                                      className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                                                    >
                                                      <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    <Dialog
                      open={!!selectedHotelContract}
                      onOpenChange={(open) => {
                        if (!open) setSelectedHotelContract(null);
                      }}
                    >
                      <DialogContent className="max-w-[96vw] lg:max-w-[900px] max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle style={{ color: "#5B9BD5" }}>
                            Contract Details test
                          </DialogTitle>
                        </DialogHeader>
                        {selectedHotelContract && (
                          <div className="space-y-4">
                            <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant="outline"
                                    className="bg-white text-[#5B9BD5] border-[#5B9BD5]/30"
                                  >
                                    Contract
                                  </Badge>
                                  <h4 className="font-semibold text-gray-900">
                                    {selectedHotelContract.contractReferenceNo}
                                  </h4>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {selectedHotelContract.contractStartDate ||
                                    "Start date"}{" "}
                                  to{" "}
                                  {selectedHotelContract.contractEndDate ||
                                    "End date"}{" "}
                                  - {selectedHotelContract.billingCycle} -{" "}
                                  {selectedHotelContract.paymentTerms}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs uppercase tracking-wide text-gray-500">
                                  Grand Total
                                </p>
                                <p className="text-xl font-semibold text-gray-900">
                                  {selectedHotelContract.currency}{" "}
                                  {selectedHotelContract.grandTotal.toFixed(2)}
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Room Rent Total
                                </p>
                                <p className="font-semibold text-gray-900">
                                  {selectedHotelContract.currency}{" "}
                                  {selectedHotelContract.roomRentTotal.toFixed(
                                    2,
                                  )}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Tax / Service Charge
                                </p>
                                <p className="font-semibold text-gray-900">
                                  {selectedHotelContract.currency}{" "}
                                  {selectedHotelContract.taxAmount.toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Security Deposit
                                </p>
                                <p className="font-semibold text-gray-900">
                                  {selectedHotelContract.currency}{" "}
                                  {Number(
                                    selectedHotelContract.securityDeposit || 0,
                                  ).toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Notice Period
                                </p>
                                <p className="font-semibold text-gray-900">
                                  {selectedHotelContract.noticePeriodDays || 0}{" "}
                                  Days
                                </p>
                              </div>
                            </div>

                            <div className="border rounded-lg overflow-hidden">
                              <div className="grid grid-cols-4 gap-2 bg-gray-50 border-b px-3 py-2 text-xs font-semibold text-gray-600">
                                <div>Room Type</div>
                                <div className="text-center">Rooms</div>
                                <div className="text-right">Rent / Room</div>
                                <div className="text-right">Total</div>
                              </div>
                              {selectedHotelContract.roomTypeRents.map(
                                (rent) => (
                                  <div
                                    key={
                                      selectedHotelContract.id +
                                      "-" +
                                      rent.roomTypeId
                                    }
                                    className="grid grid-cols-4 gap-2 px-3 py-2 text-sm border-b last:border-b-0"
                                  >
                                    <div className="font-medium text-gray-900">
                                      {rent.roomTypeName}
                                    </div>
                                    <div className="text-center text-gray-700">
                                      {rent.numberOfRooms}
                                    </div>
                                    <div className="text-right text-gray-700">
                                      {selectedHotelContract.currency}{" "}
                                      {rent.rentPerRoom.toFixed(2)}
                                    </div>
                                    <div className="text-right font-semibold text-gray-900">
                                      {selectedHotelContract.currency}{" "}
                                      {rent.totalRent.toFixed(2)}
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Included Services
                                </p>
                                <p className="text-gray-800">
                                  {selectedHotelContract.includedServices ||
                                    "-"}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Cancellation Policy
                                </p>
                                <p className="text-gray-800">
                                  {selectedHotelContract.cancellationPolicy ||
                                    "-"}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Agreement Document
                                </p>
                                {selectedHotelContract?.agreementDocumentUrl ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleViewContractAgreement(
                                        selectedHotelContract,
                                      );
                                    }}
                                    className="truncate text-[#5B9BD5] underline text-left"
                                  >
                                    {selectedHotelContract.agreementDocumentName ||
                                      "Agreement Document Uploaded"}
                                  </button>
                                ) : (
                                  <span className="text-gray-700">-</span>
                                )}
                              </div>
                            </div>

                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                onClick={() => setSelectedHotelContract(null)}
                                style={{ background: "#5B9BD5" }}
                              >
                                Close
                              </Button>
                            </div>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>
                )}

                {isHotelProperty && hotelContracts.length > 0 && (
                  <div
                    className="space-y-3"
                    data-section="added-hotel-contracts"
                  >
                    <Label className="text-sm font-semibold text-[#1E3A5F]">
                      Added Contracts ({hotelContracts.length})
                    </Label>
                    <div className="border rounded-lg overflow-hidden bg-white">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-sky-50 border-b">
                            <tr className="text-left text-gray-700">
                              <th className="px-4 py-3 font-semibold">
                                Contract
                              </th>
                              <th className="px-4 py-3 font-semibold">
                                Reference No.
                              </th>
                              <th className="px-4 py-3 font-semibold">
                                Contract Period
                              </th>
                              <th className="px-4 py-3 font-semibold">
                                Billing
                              </th>
                              <th className="px-4 py-3 font-semibold">
                                Payment
                              </th>
                              <th className="px-4 py-3 font-semibold text-right">
                                Grand Total
                              </th>
                              <th className="px-4 py-3 font-semibold text-center">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {hotelContracts.map((contract, index) => (
                              <tr
                                key={contract.id}
                                className="border-b last:border-b-0 align-middle"
                              >
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <Badge
                                    variant="outline"
                                    className="bg-white text-[#5B9BD5] border-[#5B9BD5]/30"
                                  >
                                    Contract {index + 1}
                                  </Badge>
                                </td>
                                <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                                  {contract.contractReferenceNo || "-"}
                                </td>
                                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                  {contract.contractStartDate || "Start date"}{" "}
                                  to {contract.contractEndDate || "End date"}
                                </td>
                                <td className="px-4 py-3 text-gray-700 capitalize whitespace-nowrap">
                                  {contract.billingCycle}
                                </td>
                                <td className="px-4 py-3 text-gray-700 capitalize whitespace-nowrap">
                                  {contract.paymentTerms}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                                  {contract.currency}{" "}
                                  {(contract.grandTotal ?? 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex justify-center">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedHotelContract(contract);
                                      }}
                                      className="text-[#5B9BD5] hover:bg-sky-100 h-6 w-6 p-0"
                                      title="View Contract Details"
                                    >
                                      <Eye className="w-3 h-3" />
                                    </Button>
                                    {isEditMode && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleEditHotelContract(contract)
                                        }
                                        className="text-[#5B9BD5] hover:bg-sky-100 h-6 w-6 p-0"
                                        title="Edit Contract"
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </Button>
                                    )}
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleRemoveHotelContract(contract.id)
                                      }
                                      className="text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                                      title="Remove Contract"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {!isHotelProperty && (
                  <>
                    {/* Room Type Configuration */}
                    <div className="space-y-4" data-section="room-config">
                      <div className="border-b pb-4 space-y-3">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="min-w-0">
                            <h3
                              className="text-base font-semibold flex items-center gap-2 whitespace-nowrap"
                              style={{ color: "#5B9BD5" }}
                            >
                              <DoorOpen className="w-5 h-5" />
                              Room Type Configuration
                              {propertyData.propertyType !== "Faiz" &&
                                propertyData.propertyType !== "" && (
                                  <span className="text-red-500">*</span>
                                )}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {roomTypeConfigs.length > 0
                                ? "Review added room configurations below."
                                : propertyData.propertyType !== "Faiz" &&
                                    propertyData.propertyType !== ""
                                  ? "Add at least one room type configuration (Required)"
                                  : "Add different room types for this property"}
                            </p>
                          </div>
                          <Button
                            type="button"
                            onClick={() => {
                              setIsEditingRoomTypeConfig(false);
                              setCurrentRoomTypeConfig({
                                id: "",
                                roomTypeIds: [""],
                                pricePerRoom: 0,
                                maxOccupancy: 2,
                                maxAdults: 2,
                                maxChildren: 0,
                                minAdults: 1,
                                minOccupancy: 1,
                                childAgeMax: 12,
                                numberOfRooms: 1,
                                roomNames: [],
                                personTypes: [],
                                amenities: roomAmenities.map((a) => ({
                                  ...a,
                                  isSelected: false,
                                })),
                              });
                              setIsRoomTypeDialogOpen(true);
                            }}
                            style={{ background: "#5B9BD5" }}
                            className="text-white lg:w-36"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Room
                          </Button>
                        </div>
                      </div>

                      {/* Add Room Type Form */}
                      <Dialog
                        open={isRoomTypeDialogOpen}
                        onOpenChange={(open) => {
                          setIsRoomTypeDialogOpen(open);
                          if (!open) {
                            setIsEditingRoomTypeConfig(false);
                          }
                        }}
                      >
                        <DialogContent className="max-w-[96vw] lg:max-w-[1100px] max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle style={{ color: "#5B9BD5" }}>
                              {isEditingRoomTypeConfig
                                ? "Edit Room"
                                : "Add Room"}
                            </DialogTitle>
                          </DialogHeader>
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-800 leading-snug break-words">
                              <strong>Pricing Configuration Moved:</strong> Room
                              pricing is now managed centrally. Please use the{" "}
                              <strong>Room Type Pricing Configuration</strong>{" "}
                              or <strong>Lawazim Configuration</strong> modules
                              to set prices for these room types.
                            </p>
                          </div>
                          <Card className="border-[#5B9BD5]/30 bg-white">
                            <CardContent className="p-4 space-y-4">
                              {/* Floor Type, Room Type */}
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                {/* <div className="space-y-2">
                        <SelectFieldset label="Floor Type" required>
                          {isEditingRoomTypeConfig ? (
                            <Select
                              value={currentRoomTypeConfig.floorTypes[0] || ""}
                              onValueChange={(value) =>
                                setCurrentRoomTypeConfig({
                                  ...currentRoomTypeConfig,
                                  floorTypes: [value],
                                })
                              }
                            >
                              <SelectTriggerUI>
                                <SelectValue placeholder="Select floor type" />
                              </SelectTriggerUI>
                              <SelectContent>
                                {floorTypeOptions.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                        ) : (
                          <MultiSelect
                            options={floorTypeOptions}
                            selected={currentRoomTypeConfig.floorTypes}
                            onChange={(selected) =>
                              setCurrentRoomTypeConfig({
                                ...currentRoomTypeConfig,
                                floorTypes: selected,
                              })
                            }
                            closeOnSelect // ✅ ADD THIS (if supported)
                            placeholder="Select floor types..."
                          />
                        )}
                        </SelectFieldset>
                      </div> */}

                                <div className="space-y-2">
                                  <SelectFieldset label="Room Type" required>
                                    <Select
                                      value={
                                        currentRoomTypeConfig.roomTypeIds[0] ||
                                        ""
                                      }
                                      onValueChange={(value) =>
                                        setCurrentRoomTypeConfig({
                                          ...currentRoomTypeConfig,
                                          roomTypeIds: [value],
                                        })
                                      }
                                    >
                                      <SelectTriggerUI>
                                        <SelectValue placeholder="Select room type..." />
                                      </SelectTriggerUI>
                                      <SelectContent>
                                        {getAvailableRoomTypeOptions().map(
                                          (option) => (
                                            <SelectItem
                                              key={option.value}
                                              value={option.value.toString()}
                                            >
                                              {option.label}
                                            </SelectItem>
                                          ),
                                        )}
                                      </SelectContent>
                                    </Select>
                                  </SelectFieldset>
                                </div>

                                <div className="space-y-2">
                                  <InputFieldset
                                    label="Number of Rooms"
                                    required
                                    id="config-numberOfRooms"
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={
                                      currentRoomTypeConfig.numberOfRooms || ""
                                    }
                                    onChange={(e) => {
                                      let roomCount = parseInt(e.target.value);

                                      if (isNaN(roomCount)) roomCount = 1;
                                      if (roomCount < 1) roomCount = 1;
                                      if (roomCount > 200) roomCount = 200;

                                      setCurrentRoomTypeConfig((prev) => {
                                        const updatedRoomNames = [
                                          ...(prev.roomNames || []),
                                        ];

                                        // expand
                                        while (
                                          updatedRoomNames.length < roomCount
                                        ) {
                                          updatedRoomNames.push("");
                                        }

                                        // shrink
                                        if (
                                          updatedRoomNames.length > roomCount
                                        ) {
                                          updatedRoomNames.length = roomCount;
                                        }

                                        return {
                                          ...prev,
                                          numberOfRooms: roomCount,
                                          roomNames: updatedRoomNames,
                                        };
                                      });
                                    }}
                                    placeholder="1"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <InputFieldset
                                    label="Child Age Max (Years)"
                                    id="config-childAgeMax"
                                    type="number"
                                    min="0"
                                    max="200"
                                    value={
                                      currentRoomTypeConfig.childAgeMax || ""
                                    }
                                    onChange={(e) => {
                                      let childAgeMax = parseInt(
                                        e.target.value,
                                      );

                                      if (isNaN(childAgeMax)) childAgeMax = 1;
                                      if (childAgeMax < 1) childAgeMax = 1;
                                      if (childAgeMax > 200) childAgeMax = 200;

                                      setCurrentRoomTypeConfig({
                                        ...currentRoomTypeConfig,
                                        childAgeMax,
                                      });
                                    }}
                                    placeholder="12"
                                  />
                                </div>

                                {isHotelProperty && (
                                  <div className="space-y-2">
                                    <InputFieldset
                                      label={`Rent Per Room (${hotelContract.currency})`}
                                      id="config-pricePerRoom"
                                      type="number"
                                      min="0"
                                      value={
                                        currentRoomTypeConfig.pricePerRoom || ""
                                      }
                                      onChange={(e) =>
                                        setCurrentRoomTypeConfig({
                                          ...currentRoomTypeConfig,
                                          pricePerRoom: Number(
                                            e.target.value || 0,
                                          ),
                                        })
                                      }
                                      placeholder="0.00"
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Room Names Input (appears when numberOfRooms > 0) */}
                              {currentRoomTypeConfig.numberOfRooms > 0 && (
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium">
                                    Room Names{" "}
                                    <span className="text-xs text-gray-500 font-normal">
                                      (Optional - manually name each room)
                                    </span>
                                  </Label>

                                  <div
                                    className={`grid grid-cols-2 md:grid-cols-5 gap-3 p-3 border rounded-lg ${
                                      currentRoomTypeConfig.numberOfRooms > 20
                                        ? "h-80 overflow-y-auto"
                                        : ""
                                    }`}
                                  >
                                    {Array.from({
                                      length:
                                        currentRoomTypeConfig.numberOfRooms,
                                    }).map((_, index) => (
                                      <div key={index} className="space-y-1">
                                        <InputFieldset
                                          label={`Room ${index + 1}`}
                                          required
                                          id={`room-name-${index}`}
                                          type="text"
                                          onKeyDown={(e) => {
                                            const columns =
                                              window.innerWidth >= 768 ? 5 : 2;

                                            let nextIndex = index;

                                            switch (e.key) {
                                              case "ArrowRight":
                                                nextIndex = index + 1;
                                                break;

                                              case "ArrowLeft":
                                                nextIndex = index - 1;
                                                break;

                                              case "ArrowDown":
                                                nextIndex = index + columns;
                                                break;

                                              case "ArrowUp":
                                                nextIndex = index - columns;
                                                break;

                                              default:
                                                return;
                                            }

                                            if (
                                              nextIndex >= 0 &&
                                              nextIndex <
                                                currentRoomTypeConfig.numberOfRooms
                                            ) {
                                              e.preventDefault();

                                              const nextInput =
                                                document.getElementById(
                                                  `room-name-${nextIndex}`,
                                                ) as HTMLInputElement | null;

                                              nextInput?.focus();
                                            }
                                          }}
                                          value={
                                            currentRoomTypeConfig.roomNames?.[
                                              index
                                            ] || ""
                                          }
                                          onChange={(e) => {
                                            const value = e.target.value;

                                            setCurrentRoomTypeConfig((prev) => {
                                              const updatedRoomNames = [
                                                ...(prev.roomNames || []),
                                              ];

                                              while (
                                                updatedRoomNames.length <
                                                prev.numberOfRooms
                                              ) {
                                                updatedRoomNames.push("");
                                              }

                                              updatedRoomNames[index] = value;

                                              return {
                                                ...prev,
                                                roomNames: updatedRoomNames,
                                              };
                                            });
                                          }}
                                          placeholder={`e.g., ${index + 101}`}
                                          className="text-sm"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Occupancy */}
                              <div className="grid grid-cols-1 md:grid-cols-[0.75fr_0.85fr_0.95fr_1.35fr_1.4fr] gap-3">
                                <div className="space-y-2">
                                  <InputFieldset
                                    label="Min. Adults"
                                    required
                                    id="config-minAdults"
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={
                                      currentRoomTypeConfig.minAdults || ""
                                    }
                                    onChange={(e) => {
                                      let minAdults = parseInt(e.target.value);

                                      if (isNaN(minAdults)) minAdults = 1;
                                      if (minAdults < 1) minAdults = 1;
                                      if (minAdults > 200) minAdults = 200;

                                      setCurrentRoomTypeConfig({
                                        ...currentRoomTypeConfig,
                                        minAdults,
                                      });
                                    }}
                                    placeholder="1"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <InputFieldset
                                    label="Max Adults"
                                    required
                                    id="config-maxAdults"
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={
                                      currentRoomTypeConfig.maxAdults || ""
                                    }
                                    onChange={(e) => {
                                      let adults = parseInt(e.target.value);

                                      if (isNaN(adults)) adults = 1;
                                      if (adults < 1) adults = 1;
                                      if (adults > 200) adults = 200;

                                      setCurrentRoomTypeConfig({
                                        ...currentRoomTypeConfig,
                                        maxAdults: adults,
                                        maxOccupancy: Math.min(
                                          200,
                                          adults +
                                            currentRoomTypeConfig.maxChildren,
                                        ),
                                      });
                                    }}
                                    placeholder="2"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <InputFieldset
                                    label="Max Children"
                                    required
                                    id="config-maxChildren"
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={
                                      currentRoomTypeConfig.maxChildren || ""
                                    }
                                    onChange={(e) => {
                                      let children = parseInt(e.target.value);

                                      if (isNaN(children)) children = 1;
                                      if (children < 1) children = 1;
                                      if (children > 200) children = 200;

                                      setCurrentRoomTypeConfig({
                                        ...currentRoomTypeConfig,
                                        maxChildren: children,
                                        maxOccupancy: Math.min(
                                          200,
                                          currentRoomTypeConfig.maxAdults +
                                            children,
                                        ),
                                      });
                                    }}
                                    placeholder="2"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <InputFieldset
                                    label="Min. Occupancy (Total)"
                                    required
                                    id="config-minOccupancy"
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={
                                      currentRoomTypeConfig.minOccupancy || ""
                                    }
                                    onChange={(e) => {
                                      let minOccupancy = parseInt(
                                        e.target.value,
                                      );

                                      if (isNaN(minOccupancy)) minOccupancy = 1;
                                      if (minOccupancy < 1) minOccupancy = 1;
                                      if (minOccupancy > 200)
                                        minOccupancy = 200;

                                      setCurrentRoomTypeConfig({
                                        ...currentRoomTypeConfig,
                                        minOccupancy,
                                      });
                                    }}
                                    placeholder="1"
                                  />
                                </div>

                                <div className="space-y-2">
                                  {/* <InputFieldset
                                    label="Max Occupancy (Total)"
                                    id="config-maxOccupancy"
                                    type="number"
                                    value={currentRoomTypeConfig.maxOccupancy}
                                    disabled
                                    className="bg-gray-100"
                                  /> */}
                                  <InputFieldset
                                    label="Max Occupancy (Total)"
                                    id="config-maxOccupancy"
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={currentRoomTypeConfig.maxOccupancy}
                                    onChange={(e) => {
                                      let occupancy = parseInt(e.target.value);

                                      if (isNaN(occupancy)) occupancy = 1;
                                      if (occupancy < 1) occupancy = 1;
                                      if (occupancy > 200) occupancy = 200;

                                      setCurrentRoomTypeConfig({
                                        ...currentRoomTypeConfig,
                                        maxOccupancy: occupancy,
                                      });
                                    }}
                                  />
                                </div>

                                {/* <div className="space-y-2">
                        <InputFieldset
                          label="Max Extra Beds Allowed"
                          id="config-maxExtraBeds"
                          type="number"
                          min="0"
                          value={currentRoomTypeConfig.maxExtraBeds || ""}
                          onChange={(e) =>
                            setCurrentRoomTypeConfig({
                              ...currentRoomTypeConfig,
                              maxExtraBeds: parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="0"
                        />
                      </div> */}
                              </div>

                              {/* Room Amenities */}
                              <Accordion
                                type="single"
                                collapsible
                                className="w-full border rounded-lg"
                              >
                                <AccordionItem
                                  value="room-amenities"
                                  className="border-0"
                                >
                                  <AccordionTrigger className="px-4 py-3 hover:no-underline bg-gradient-to-r from-sky-50 to-blue-50 rounded-t-lg w-full [&>svg]:ml-auto">
                                    <h4
                                      className="text-sm font-semibold"
                                      style={{ color: "#5B9BD5" }}
                                    >
                                      Room Amenities
                                    </h4>
                                  </AccordionTrigger>
                                  <AccordionContent className="px-0 pb-0">
                                    <div className="px-4 pt-3">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 bg-gray-50 border rounded-lg">
                                        {(
                                          currentRoomTypeConfig.amenities || []
                                        ).map((amenity) => (
                                          <div
                                            key={amenity.id}
                                            className="flex items-center justify-between gap-3 p-2 bg-white rounded border hover:border-[#5B9BD5]/30 transition-colors"
                                          >
                                            <label className="text-xs text-gray-700">
                                              {amenity.name}
                                            </label>
                                            <Checkbox
                                              checked={amenity.isSelected}
                                              onCheckedChange={(checked) => {
                                                setCurrentRoomTypeConfig(
                                                  (prev) => ({
                                                    ...prev,
                                                    amenities: (
                                                      prev.amenities || []
                                                    ).map((a) =>
                                                      a.id === amenity.id
                                                        ? {
                                                            ...a,
                                                            isSelected:
                                                              checked as boolean,
                                                          }
                                                        : a,
                                                    ),
                                                  }),
                                                );
                                              }}
                                            />
                                          </div>
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-3">
                                        * Select amenities available in this
                                        room type.
                                      </p>
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              </Accordion>

                              {/* Add/Update Button */}
                              {isEditingRoomTypeConfig ? (
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      setIsEditingRoomTypeConfig(false);
                                      setCurrentRoomTypeConfig({
                                        id: "",
                                        roomTypeIds: [""],
                                        pricePerRoom: 0,
                                        maxOccupancy: 2,
                                        maxAdults: 2,
                                        maxChildren: 0,
                                        minAdults: 1,
                                        minOccupancy: 1,
                                        childAgeMax: 12,
                                        numberOfRooms: 1,
                                        roomNames: [],
                                        personTypes: [],
                                        amenities: [],
                                      });
                                      setIsRoomTypeDialogOpen(false);
                                      toast.info("Edit cancelled");
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                  >
                                    <X className="w-4 h-4 mr-2" />
                                    Cancel
                                  </Button>
                                  {isEditMode && (
                                    <Button
                                      type="button"
                                      onClick={handleAddRoomTypeConfig}
                                      className="flex-1"
                                      style={{ background: "#5B9BD5" }}
                                    >
                                      <Edit2 className="w-4 h-4 mr-2" />
                                      Update Room
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  onClick={handleAddRoomTypeConfig}
                                  className="w-full"
                                  style={{ background: "#5B9BD5" }}
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add Room
                                </Button>
                              )}
                            </CardContent>
                          </Card>
                        </DialogContent>
                      </Dialog>

                      {isHotelProperty && (
                        <div
                          className="space-y-4"
                          data-section="hotel-room-rent"
                        >
                          <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
                            <div className="p-5 space-y-5">
                              <div className="border rounded-lg overflow-hidden">
                                <div className="px-4 py-3 bg-amber-50 border-b flex flex-col md:flex-row md:items-center justify-between gap-2">
                                  <div>
                                    <h4 className="font-semibold text-gray-900">
                                      Room Configurations
                                    </h4>
                                    <p className="text-xs text-gray-500">
                                      Room rent is entered in the Add/Edit Room
                                      popup for hotel properties.
                                    </p>
                                  </div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    Room Rent Total: {hotelContract.currency}{" "}
                                    {hotelRoomRentTotal.toFixed(2)}
                                  </div>
                                </div>

                                {roomTypeConfigs.length > 0 ? (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gray-50 border-b">
                                        <tr className="text-left text-gray-700">
                                          <th className="px-4 py-3 font-semibold">
                                            Room Type
                                          </th>
                                          <th className="px-4 py-3 font-semibold text-center">
                                            Rooms
                                          </th>
                                          <th className="px-4 py-3 font-semibold">
                                            Capacity
                                          </th>
                                          <th className="px-4 py-3 font-semibold">
                                            Child Age
                                          </th>
                                          <th className="px-4 py-3 font-semibold">
                                            Room Names
                                          </th>
                                          <th className="px-4 py-3 font-semibold">
                                            Amenities
                                          </th>
                                          <th className="px-4 py-3 font-semibold">
                                            Rent Per Room
                                          </th>
                                          <th className="px-4 py-3 font-semibold text-right">
                                            Total Rent
                                          </th>
                                          <th className="px-4 py-3 font-semibold text-center">
                                            Action
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {roomTypeConfigs.map((config) => {
                                          const rentPerRoom = Number(
                                            config.pricePerRoom || 0,
                                          );
                                          const rowTotal =
                                            rentPerRoom *
                                            Number(config.numberOfRooms || 0);
                                          const roomNames = (
                                            config.roomNames || []
                                          ).filter(Boolean);
                                          const propertyAmenitiesList =
                                            getPropertyAmenities();
                                          const roomAmenitiesList =
                                            getRoomTypeAmenities(config);
                                          const amenitiesList = [
                                            ...propertyAmenitiesList,
                                            ...roomAmenitiesList,
                                          ];

                                          return (
                                            <tr
                                              key={config.id}
                                              className="border-b last:border-b-0"
                                            >
                                              <td className="px-4 py-3 font-medium text-gray-900">
                                                {getRoomTypeName(
                                                  config.roomTypeIds[0],
                                                )}
                                              </td>
                                              <td className="px-4 py-3 text-center text-gray-700">
                                                {config.numberOfRooms}
                                              </td>
                                              <td className="px-4 py-3 text-gray-700 min-w-[120px]">
                                                <div className="font-medium text-gray-900">
                                                  Min:{" "}
                                                  {config.minOccupancy ||
                                                    config.minAdults ||
                                                    1}{" "}
                                                  / Max: {config.maxOccupancy}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                  {config.maxAdults}A
                                                  {config.maxChildren > 0
                                                    ? " + " +
                                                      config.maxChildren +
                                                      "C"
                                                    : ""}
                                                </div>
                                              </td>
                                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                                {config.childAgeMax ||
                                                  propertyData.childAgeMax ||
                                                  0}{" "}
                                                Yrs
                                              </td>
                                              <td className="px-4 py-3 text-gray-700 min-w-[160px]">
                                                {roomNames.length > 0
                                                  ? roomNames.join(", ")
                                                  : "-"}
                                              </td>
                                              <td className="px-4 py-3 min-w-[180px]">
                                                <div className="flex flex-wrap gap-1">
                                                  {amenitiesList.length > 0 ? (
                                                    amenitiesList.map(
                                                      (amenity, index) => (
                                                        <Badge
                                                          key={
                                                            config.id +
                                                            "-amenity-" +
                                                            index
                                                          }
                                                          variant="outline"
                                                          className="bg-[#5B9BD5]/10 text-[#2563eb] border-[#5B9BD5]/30"
                                                        >
                                                          {amenity}
                                                        </Badge>
                                                      ),
                                                    )
                                                  ) : (
                                                    <span className="text-gray-500">
                                                      -
                                                    </span>
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                                {hotelContract.currency}{" "}
                                                {rentPerRoom.toFixed(2)}
                                              </td>
                                              <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                                                {hotelContract.currency}{" "}
                                                {rowTotal.toFixed(2)}
                                              </td>
                                              <td className="px-4 py-3">
                                                <div className="flex justify-center gap-1">
                                                  {isEditMode && (
                                                    <Button
                                                      type="button"
                                                      size="sm"
                                                      variant="ghost"
                                                      onClick={() =>
                                                        handleEditRoomTypeConfig(
                                                          config,
                                                        )
                                                      }
                                                      className="text-[#5B9BD5] hover:bg-[#5B9BD5]/10 h-8 w-8 p-0"
                                                    >
                                                      <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                  )}
                                                  <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() =>
                                                      handleRemoveRoomTypeConfig(
                                                        config.id,
                                                      )
                                                    }
                                                    className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                                                  >
                                                    <Trash2 className="w-4 h-4" />
                                                  </Button>
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="p-4 text-sm text-amber-800 bg-amber-50 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    Please add at least one room type
                                    configuration below before saving the
                                    property.
                                  </div>
                                )}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-4 rounded-lg bg-gray-50 border">
                                  <p className="text-xs uppercase tracking-wide text-gray-500">
                                    Room Rent Total
                                  </p>
                                  <p className="text-xl font-semibold text-gray-900">
                                    {hotelContract.currency}{" "}
                                    {hotelRoomRentTotal.toFixed(2)}
                                  </p>
                                </div>
                                <div className="p-4 rounded-lg bg-gray-50 border">
                                  <p className="text-xs uppercase tracking-wide text-gray-500">
                                    Tax / Service Charge
                                  </p>
                                  <p className="text-xl font-semibold text-gray-900">
                                    {hotelContract.currency}{" "}
                                    {hotelTaxAmount.toFixed(2)}
                                  </p>
                                </div>
                                <div className="p-4 rounded-lg bg-sky-50 border border-sky-200">
                                  <p className="text-xs uppercase tracking-wide text-sky-700">
                                    Contract Grand Total
                                  </p>
                                  <p className="text-xl font-semibold text-gray-900">
                                    {hotelContract.currency}{" "}
                                    {hotelContractGrandTotal.toFixed(2)}
                                  </p>
                                </div>
                              </div>

                              <div className="flex flex-col sm:flex-row justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    resetHotelContractForm();
                                    setEditingHotelContractId(null);
                                    setRoomTypeConfigs([]);
                                  }}
                                  className="sm:w-36"
                                >
                                  Clear Contract
                                </Button>
                                <Button
                                  type="button"
                                  onClick={handleAddHotelContract}
                                  className="sm:w-40"
                                  style={{ background: "#5B9BD5" }}
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  {hotelContract.hotelContractId
                                    ? "Update Contract"
                                    : "Add Contract"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {isHotelProperty && hotelContracts.length > 0 && (
                        <div
                          className="space-y-3"
                          data-section="added-hotel-contracts"
                        >
                          <Label className="text-sm font-semibold text-[#1E3A5F]">
                            Added Contracts ({hotelContracts.length})
                          </Label>
                          {hotelContracts.map((contract, index) => (
                            <Card
                              key={contract.id}
                              className="border-[#5B9BD5]/30 shadow-sm rounded-xl overflow-hidden"
                            >
                              <CardContent style={{ padding: "0px" }}>
                                <div className="p-4 bg-sky-50 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <Badge
                                        variant="outline"
                                        className="bg-white text-[#5B9BD5] border-[#5B9BD5]/30"
                                      >
                                        Contract {index + 1}
                                      </Badge>
                                      <h4 className="font-semibold text-gray-900">
                                        {contract.contractReferenceNo}
                                      </h4>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                      {contract.contractStartDate ||
                                        "Start date"}{" "}
                                      to{" "}
                                      {contract.contractEndDate || "End date"} -{" "}
                                      {contract.billingCycle} -{" "}
                                      {contract.paymentTerms}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="text-right mr-2">
                                      <p className="text-xs uppercase tracking-wide text-gray-500">
                                        Grand Total
                                      </p>
                                      <p className="text-lg font-semibold text-gray-900">
                                        {contract.currency}{" "}
                                        {(contract.grandTotal ?? 0).toFixed(2)}
                                      </p>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        setSelectedHotelContract(contract)
                                      }
                                      className="text-[#5B9BD5] hover:bg-sky-100 h-8 w-8 p-0"
                                      title="View Contract Details"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    {isEditMode && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleEditHotelContract(contract)
                                        }
                                        className="text-[#5B9BD5] hover:bg-sky-100 h-8 w-8 p-0"
                                        title="Edit Contract"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleRemoveHotelContract(contract.id)
                                      }
                                      className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                                      title="Remove Contract"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}

                      {roomTypeConfigs.length === 0 &&
                        !isHotelProperty &&
                        propertyData.propertyType !== "Faiz" &&
                        propertyData.propertyType !== "" && (
                          <div className="border rounded-lg overflow-hidden bg-white">
                            <div className="p-4 text-sm text-amber-800 bg-amber-50 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 flex-shrink-0" />
                              Please add at least one room type configuration
                              below before saving the property.
                            </div>
                          </div>
                        )}

                      <Dialog
                        open={!!selectedHotelContract}
                        onOpenChange={(open) => {
                          if (!open) setSelectedHotelContract(null);
                        }}
                      >
                        <DialogContent className="max-w-[96vw] lg:max-w-[900px] max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle style={{ color: "#5B9BD5" }}>
                              Contract Details
                            </DialogTitle>
                          </DialogHeader>
                          {selectedHotelContract && (
                            <div className="space-y-4">
                              <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Badge
                                      variant="outline"
                                      className="bg-white text-[#5B9BD5] border-[#5B9BD5]/30"
                                    >
                                      Contract
                                    </Badge>
                                    <h4 className="font-semibold text-gray-900">
                                      {
                                        selectedHotelContract.contractReferenceNo
                                      }
                                    </h4>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {selectedHotelContract.contractStartDate ||
                                      "Start date"}{" "}
                                    to{" "}
                                    {selectedHotelContract.contractEndDate ||
                                      "End date"}{" "}
                                    - {selectedHotelContract.billingCycle} -{" "}
                                    {selectedHotelContract.paymentTerms}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs uppercase tracking-wide text-gray-500">
                                    Grand Total
                                  </p>
                                  <p className="text-xl font-semibold text-gray-900">
                                    {selectedHotelContract.currency}{" "}
                                    {selectedHotelContract.grandTotal.toFixed(
                                      2,
                                    )}
                                  </p>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-xs font-medium text-gray-500">
                                    Room Rent Total
                                  </p>
                                  <p className="font-semibold text-gray-900">
                                    {selectedHotelContract.currency}{" "}
                                    {selectedHotelContract.roomRentTotal.toFixed(
                                      2,
                                    )}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500">
                                    Tax / Service Charge
                                  </p>
                                  <p className="font-semibold text-gray-900">
                                    {selectedHotelContract.currency}{" "}
                                    {selectedHotelContract.taxAmount.toFixed(2)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500">
                                    Security Deposit
                                  </p>
                                  <p className="font-semibold text-gray-900">
                                    {selectedHotelContract.currency}{" "}
                                    {Number(
                                      selectedHotelContract.securityDeposit ||
                                        0,
                                    ).toFixed(2)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500">
                                    Notice Period
                                  </p>
                                  <p className="font-semibold text-gray-900">
                                    {selectedHotelContract.noticePeriodDays ||
                                      0}{" "}
                                    Days
                                  </p>
                                </div>
                              </div>

                              <div className="border rounded-lg overflow-hidden">
                                <div className="grid grid-cols-4 gap-2 bg-gray-50 border-b px-3 py-2 text-xs font-semibold text-gray-600">
                                  <div>Room Type</div>
                                  <div className="text-center">Rooms</div>
                                  <div className="text-right">Rent / Room</div>
                                  <div className="text-right">Total</div>
                                </div>
                                {selectedHotelContract.roomTypeRents.map(
                                  (rent) => (
                                    <div
                                      key={
                                        selectedHotelContract.id +
                                        "-" +
                                        rent.roomTypeId
                                      }
                                      className="grid grid-cols-4 gap-2 px-3 py-2 text-sm border-b last:border-b-0"
                                    >
                                      <div className="font-medium text-gray-900">
                                        {rent.roomTypeName}
                                      </div>
                                      <div className="text-center text-gray-700">
                                        {rent.numberOfRooms}
                                      </div>
                                      <div className="text-right text-gray-700">
                                        {selectedHotelContract.currency}{" "}
                                        {rent.rentPerRoom.toFixed(2)}
                                      </div>
                                      <div className="text-right font-semibold text-gray-900">
                                        {selectedHotelContract.currency}{" "}
                                        {rent.totalRent.toFixed(2)}
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <p className="text-xs font-medium text-gray-500">
                                    Included Services
                                  </p>
                                  <p className="text-gray-800">
                                    {selectedHotelContract.includedServices ||
                                      "-"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500">
                                    Cancellation Policy
                                  </p>
                                  <p className="text-gray-800">
                                    {selectedHotelContract.cancellationPolicy ||
                                      "-"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500">
                                    Agreement Document
                                  </p>
                                  {selectedHotelContract.agreementDocumentName ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleViewContractAgreement(
                                          selectedHotelContract,
                                        )
                                      }
                                      className="text-[#5B9BD5] underline text-left truncate max-w-full"
                                    >
                                      {
                                        selectedHotelContract.agreementDocumentName
                                      }
                                    </button>
                                  ) : (
                                    <p className="text-gray-800">-</p>
                                  )}
                                </div>
                              </div>

                              <div className="flex justify-end gap-2">
                                <Button
                                  type="button"
                                  onClick={() => setSelectedHotelContract(null)}
                                  style={{ background: "#5B9BD5" }}
                                >
                                  Close
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>

                      {/* Room type configurations will be listed here */}
                      {roomTypeConfigs.length > 0 && !isHotelProperty && (
                        <div className="border rounded-lg overflow-hidden bg-white">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 border-b">
                                <tr className="text-left text-gray-700">
                                  <th className="px-4 py-3 font-semibold">
                                    Room Type
                                  </th>
                                  <th className="px-4 py-3 font-semibold text-center">
                                    Rooms
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Capacity
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Child Age
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Room Names
                                  </th>
                                  <th className="px-4 py-3 font-semibold">
                                    Amenities
                                  </th>
                                  <th className="px-4 py-3 font-semibold text-center">
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {roomTypeConfigs.map((config) => {
                                  const roomType = getRoomTypeName(
                                    config.roomTypeIds[0],
                                  );
                                  const propertyAmenitiesList =
                                    getPropertyAmenities();
                                  const roomAmenitiesList =
                                    getRoomTypeAmenities(config);
                                  const amenitiesList = [
                                    ...propertyAmenitiesList,
                                    ...roomAmenitiesList,
                                  ];
                                  const roomNames = (
                                    config.roomNames || []
                                  ).filter(Boolean);

                                  return (
                                    <tr
                                      key={config.id}
                                      className="border-b last:border-b-0 align-top"
                                    >
                                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                        {roomType}
                                      </td>
                                      <td className="px-4 py-3 text-center text-gray-700">
                                        {config.numberOfRooms}
                                      </td>
                                      <td className="px-4 py-3 text-gray-700 min-w-[120px]">
                                        <div className="font-medium text-gray-900">
                                          Min:{" "}
                                          {config.minOccupancy ||
                                            config.minAdults ||
                                            1}{" "}
                                          / Max: {config.maxOccupancy}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {config.maxAdults}A
                                          {config.maxChildren > 0
                                            ? " + " + config.maxChildren + "C"
                                            : ""}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                        {config.childAgeMax ||
                                          propertyData.childAgeMax ||
                                          0}{" "}
                                        Yrs
                                      </td>
                                      <td className="px-4 py-3 text-gray-700 min-w-[160px]">
                                        {roomNames.length > 0
                                          ? roomNames.join(", ")
                                          : "-"}
                                      </td>
                                      <td className="px-4 py-3 min-w-[220px]">
                                        <div className="flex flex-wrap gap-1">
                                          {amenitiesList.length > 0 ? (
                                            amenitiesList.map(
                                              (amenity, index) => (
                                                <Badge
                                                  key={
                                                    config.id +
                                                    "-amenity-" +
                                                    index
                                                  }
                                                  variant="outline"
                                                  className="bg-[#5B9BD5]/10 text-[#2563eb] border-[#5B9BD5]/30"
                                                >
                                                  {amenity}
                                                </Badge>
                                              ),
                                            )
                                          ) : (
                                            <span className="text-gray-500">
                                              -
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="flex justify-center gap-1">
                                          {isEditMode && (
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="ghost"
                                              onClick={() =>
                                                handleEditRoomTypeConfig(config)
                                              }
                                              className="text-[#5B9BD5] hover:bg-[#5B9BD5]/10 h-8 w-8 p-0"
                                            >
                                              <Edit2 className="w-4 h-4" />
                                            </Button>
                                          )}
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            onClick={() =>
                                              handleRemoveRoomTypeConfig(
                                                config.id,
                                              )
                                            }
                                            className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </>
  );
};
